import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

import { AccountKeyScopes } from '../enums/account-key-scopes';
import { AuthenticatorType } from '../enums/authenticator-type';
import { AuthenticationFactor } from '../enums/authentication-factor';
import { IdTokenProvider } from '../enums/id-token-provider';
import { OAuthProvider } from '../enums/o-auth-provider';
export class Account {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * Get the currently logged in user.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    get<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(): Promise<Models.User<Preferences>> {
        const apiPath = '/account';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to allow a new user to register a new account in your project. After the user registration completes successfully, you can use the [/account/verfication](https://appwrite.io/docs/references/cloud/client-web/account#createVerification) route to start verifying the user email address. To allow the new user to login to their new account, you need to create a new [account session](https://appwrite.io/docs/references/cloud/client-web/account#createEmailSession).
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.email - User email.
     * @param {string} params.password - New user password. Must be between 8 and 256 chars.
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    create<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        userId: string;
        email: string;
        password: string;
        name?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Use this endpoint to allow a new user to register a new account in your project. After the user registration completes successfully, you can use the [/account/verfication](https://appwrite.io/docs/references/cloud/client-web/account#createVerification) route to start verifying the user email address. To allow the new user to login to their new account, you need to create a new [account session](https://appwrite.io/docs/references/cloud/client-web/account#createEmailSession).
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} email - User email.
     * @param {string} password - New user password. Must be between 8 and 256 chars.
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    create<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        userId: string,
        email: string,
        password: string,
        name?: string,
    ): Promise<Models.User<Preferences>>;
    create<Preferences extends Models.Preferences = Models.DefaultPreferences>(
        paramsOrFirst:
            | { userId: string; email: string; password: string; name?: string }
            | string,
        ...rest: [string?, string?, string?]
    ): Promise<Models.User<Preferences>> {
        let params: {
            userId: string;
            email: string;
            password: string;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                password: string;
                name?: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                password: rest[1] as string,
                name: rest[2] as string,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const password = params.password;
        const name = params.name;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/account';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete the currently logged in user.
     *
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    delete(): Promise<{}> {
        const apiPath = '/account';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * List all billing addresses for a user.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, expired, failed
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddressList>}
     */
    listBillingAddresses(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.BillingAddressList>;
    /**
     * List all billing addresses for a user.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, expired, failed
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddressList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listBillingAddresses(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.BillingAddressList>;
    listBillingAddresses(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.BillingAddressList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/account/billing-addresses';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'billingAddressList',
            models: {
                billingAddressList: {
                    defaults: {
                        total: 0,
                        billingAddresses: [],
                    },
                    properties: {
                        billingAddresses: {
                            items: 'billingAddress',
                        },
                    },
                },
                billingAddress: {
                    defaults: {
                        $id: '',
                        userId: '',
                        streetAddress: '',
                        country: '',
                        city: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add a new billing address to a user's account.
     *
     * @param {string} params.country - Country
     * @param {string} params.city - City
     * @param {string} params.streetAddress - Street address
     * @param {string | null} params.addressLine2 - Address line 2
     * @param {string | null} params.state - State or province
     * @param {string | null} params.postalCode - Postal code
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddress>}
     */
    createBillingAddress(params: {
        country: string;
        city: string;
        streetAddress: string;
        addressLine2?: string | null;
        state?: string | null;
        postalCode?: string | null;
    }): Promise<Models.BillingAddress>;
    /**
     * Add a new billing address to a user's account.
     *
     * @param {string} country - Country
     * @param {string} city - City
     * @param {string} streetAddress - Street address
     * @param {string | null} addressLine2 - Address line 2
     * @param {string | null} state - State or province
     * @param {string | null} postalCode - Postal code
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddress>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createBillingAddress(
        country: string,
        city: string,
        streetAddress: string,
        addressLine2?: string | null,
        state?: string | null,
        postalCode?: string | null,
    ): Promise<Models.BillingAddress>;
    createBillingAddress(
        paramsOrFirst:
            | {
                  country: string;
                  city: string;
                  streetAddress: string;
                  addressLine2?: string | null;
                  state?: string | null;
                  postalCode?: string | null;
              }
            | string,
        ...rest: [
            string?,
            string?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
        ]
    ): Promise<Models.BillingAddress> {
        let params: {
            country: string;
            city: string;
            streetAddress: string;
            addressLine2?: string | null;
            state?: string | null;
            postalCode?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                country: string;
                city: string;
                streetAddress: string;
                addressLine2?: string | null;
                state?: string | null;
                postalCode?: string | null;
            };
        } else {
            params = {
                country: paramsOrFirst as string,
                city: rest[0] as string,
                streetAddress: rest[1] as string,
                addressLine2: rest[2] as string | null,
                state: rest[3] as string | null,
                postalCode: rest[4] as string | null,
            };
        }

        const country = params.country;
        const city = params.city;
        const streetAddress = params.streetAddress;
        const addressLine2 = params.addressLine2;
        const state = params.state;
        const postalCode = params.postalCode;

        if (typeof country === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "country"',
            );
        }
        if (typeof city === 'undefined') {
            throw new AppwriteException('Missing required parameter: "city"');
        }
        if (typeof streetAddress === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "streetAddress"',
            );
        }
        const apiPath = '/account/billing-addresses';
        const apiPayload: Payload = {};
        if (typeof country !== 'undefined') {
            apiPayload['country'] = country;
        }
        if (typeof city !== 'undefined') {
            apiPayload['city'] = city;
        }
        if (typeof streetAddress !== 'undefined') {
            apiPayload['streetAddress'] = streetAddress;
        }
        if (typeof addressLine2 !== 'undefined') {
            apiPayload['addressLine2'] = addressLine2;
        }
        if (typeof state !== 'undefined') {
            apiPayload['state'] = state;
        }
        if (typeof postalCode !== 'undefined') {
            apiPayload['postalCode'] = postalCode;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'billingAddress',
            models: {
                billingAddress: {
                    defaults: {
                        $id: '',
                        userId: '',
                        streetAddress: '',
                        country: '',
                        city: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a specific billing address for a user using it's ID.
     *
     * @param {string} params.billingAddressId - Unique ID of billing address
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddress>}
     */
    getBillingAddress(params: {
        billingAddressId: string;
    }): Promise<Models.BillingAddress>;
    /**
     * Get a specific billing address for a user using it's ID.
     *
     * @param {string} billingAddressId - Unique ID of billing address
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddress>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getBillingAddress(billingAddressId: string): Promise<Models.BillingAddress>;
    getBillingAddress(
        paramsOrFirst: { billingAddressId: string } | string,
    ): Promise<Models.BillingAddress> {
        let params: { billingAddressId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { billingAddressId: string };
        } else {
            params = {
                billingAddressId: paramsOrFirst as string,
            };
        }

        const billingAddressId = params.billingAddressId;

        if (
            typeof billingAddressId === 'undefined' ||
            billingAddressId === ''
        ) {
            throw new AppwriteException(
                'Missing required parameter: "billingAddressId"',
            );
        }
        const apiPath = '/account/billing-addresses/{billingAddressId}'.replace(
            '{billingAddressId}',
            encodeURIComponent(String(billingAddressId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'billingAddress',
            models: {
                billingAddress: {
                    defaults: {
                        $id: '',
                        userId: '',
                        streetAddress: '',
                        country: '',
                        city: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a specific billing address using it's ID.
     *
     * @param {string} params.billingAddressId - Unique ID of billing address
     * @param {string} params.country - Country
     * @param {string} params.city - City
     * @param {string} params.streetAddress - Street address
     * @param {string | null} params.addressLine2 - Address line 2
     * @param {string | null} params.state - State or province
     * @param {string | null} params.postalCode - Postal code
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddress>}
     */
    updateBillingAddress(params: {
        billingAddressId: string;
        country: string;
        city: string;
        streetAddress: string;
        addressLine2?: string | null;
        state?: string | null;
        postalCode?: string | null;
    }): Promise<Models.BillingAddress>;
    /**
     * Update a specific billing address using it's ID.
     *
     * @param {string} billingAddressId - Unique ID of billing address
     * @param {string} country - Country
     * @param {string} city - City
     * @param {string} streetAddress - Street address
     * @param {string | null} addressLine2 - Address line 2
     * @param {string | null} state - State or province
     * @param {string | null} postalCode - Postal code
     * @throws {AppwriteException}
     * @returns {Promise<Models.BillingAddress>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateBillingAddress(
        billingAddressId: string,
        country: string,
        city: string,
        streetAddress: string,
        addressLine2?: string | null,
        state?: string | null,
        postalCode?: string | null,
    ): Promise<Models.BillingAddress>;
    updateBillingAddress(
        paramsOrFirst:
            | {
                  billingAddressId: string;
                  country: string;
                  city: string;
                  streetAddress: string;
                  addressLine2?: string | null;
                  state?: string | null;
                  postalCode?: string | null;
              }
            | string,
        ...rest: [
            string?,
            string?,
            string?,
            (string | null)?,
            (string | null)?,
            (string | null)?,
        ]
    ): Promise<Models.BillingAddress> {
        let params: {
            billingAddressId: string;
            country: string;
            city: string;
            streetAddress: string;
            addressLine2?: string | null;
            state?: string | null;
            postalCode?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                billingAddressId: string;
                country: string;
                city: string;
                streetAddress: string;
                addressLine2?: string | null;
                state?: string | null;
                postalCode?: string | null;
            };
        } else {
            params = {
                billingAddressId: paramsOrFirst as string,
                country: rest[0] as string,
                city: rest[1] as string,
                streetAddress: rest[2] as string,
                addressLine2: rest[3] as string | null,
                state: rest[4] as string | null,
                postalCode: rest[5] as string | null,
            };
        }

        const billingAddressId = params.billingAddressId;
        const country = params.country;
        const city = params.city;
        const streetAddress = params.streetAddress;
        const addressLine2 = params.addressLine2;
        const state = params.state;
        const postalCode = params.postalCode;

        if (
            typeof billingAddressId === 'undefined' ||
            billingAddressId === ''
        ) {
            throw new AppwriteException(
                'Missing required parameter: "billingAddressId"',
            );
        }
        if (typeof country === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "country"',
            );
        }
        if (typeof city === 'undefined') {
            throw new AppwriteException('Missing required parameter: "city"');
        }
        if (typeof streetAddress === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "streetAddress"',
            );
        }
        const apiPath = '/account/billing-addresses/{billingAddressId}'.replace(
            '{billingAddressId}',
            encodeURIComponent(String(billingAddressId)),
        );
        const apiPayload: Payload = {};
        if (typeof country !== 'undefined') {
            apiPayload['country'] = country;
        }
        if (typeof city !== 'undefined') {
            apiPayload['city'] = city;
        }
        if (typeof streetAddress !== 'undefined') {
            apiPayload['streetAddress'] = streetAddress;
        }
        if (typeof addressLine2 !== 'undefined') {
            apiPayload['addressLine2'] = addressLine2;
        }
        if (typeof state !== 'undefined') {
            apiPayload['state'] = state;
        }
        if (typeof postalCode !== 'undefined') {
            apiPayload['postalCode'] = postalCode;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'billingAddress',
            models: {
                billingAddress: {
                    defaults: {
                        $id: '',
                        userId: '',
                        streetAddress: '',
                        country: '',
                        city: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a specific billing address using it's ID.
     *
     * @param {string} params.billingAddressId - Billing address unique ID
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteBillingAddress(params: { billingAddressId: string }): Promise<{}>;
    /**
     * Delete a specific billing address using it's ID.
     *
     * @param {string} billingAddressId - Billing address unique ID
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteBillingAddress(billingAddressId: string): Promise<{}>;
    deleteBillingAddress(
        paramsOrFirst: { billingAddressId: string } | string,
    ): Promise<{}> {
        let params: { billingAddressId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { billingAddressId: string };
        } else {
            params = {
                billingAddressId: paramsOrFirst as string,
            };
        }

        const billingAddressId = params.billingAddressId;

        if (
            typeof billingAddressId === 'undefined' ||
            billingAddressId === ''
        ) {
            throw new AppwriteException(
                'Missing required parameter: "billingAddressId"',
            );
        }
        const apiPath = '/account/billing-addresses/{billingAddressId}'.replace(
            '{billingAddressId}',
            encodeURIComponent(String(billingAddressId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of the OAuth2 consents the current user has given to third-party apps.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2ConsentList>}
     */
    listConsents(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.Oauth2ConsentList>;
    /**
     * Get a list of the OAuth2 consents the current user has given to third-party apps.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2ConsentList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listConsents(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.Oauth2ConsentList>;
    listConsents(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.Oauth2ConsentList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/account/consents';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oauth2ConsentList',
            models: {
                oauth2ConsentList: {
                    defaults: {
                        total: 0,
                        consents: [],
                    },
                    properties: {
                        consents: {
                            items: 'oauth2Consent',
                        },
                    },
                },
                oauth2Consent: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        appId: '',
                        cimdUrl: '',
                        scopes: [],
                        resources: [],
                        authorizationDetails: '[]',
                        expire: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get an OAuth2 consent the current user has given to a third-party app by its unique ID.
     *
     * @param {string} params.consentId - Consent unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2Consent>}
     */
    getConsent(params: { consentId: string }): Promise<Models.Oauth2Consent>;
    /**
     * Get an OAuth2 consent the current user has given to a third-party app by its unique ID.
     *
     * @param {string} consentId - Consent unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2Consent>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getConsent(consentId: string): Promise<Models.Oauth2Consent>;
    getConsent(
        paramsOrFirst: { consentId: string } | string,
    ): Promise<Models.Oauth2Consent> {
        let params: { consentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { consentId: string };
        } else {
            params = {
                consentId: paramsOrFirst as string,
            };
        }

        const consentId = params.consentId;

        if (typeof consentId === 'undefined' || consentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "consentId"',
            );
        }
        const apiPath = '/account/consents/{consentId}'.replace(
            '{consentId}',
            encodeURIComponent(String(consentId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oauth2Consent',
            models: {
                oauth2Consent: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        appId: '',
                        cimdUrl: '',
                        scopes: [],
                        resources: [],
                        authorizationDetails: '[]',
                        expire: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete an OAuth2 consent by its unique ID. All token families issued under the consent are revoked, and the app must ask for consent again to regain access.
     *
     * @param {string} params.consentId - Consent unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteConsent(params: { consentId: string }): Promise<{}>;
    /**
     * Delete an OAuth2 consent by its unique ID. All token families issued under the consent are revoked, and the app must ask for consent again to regain access.
     *
     * @param {string} consentId - Consent unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteConsent(consentId: string): Promise<{}>;
    deleteConsent(paramsOrFirst: { consentId: string } | string): Promise<{}> {
        let params: { consentId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { consentId: string };
        } else {
            params = {
                consentId: paramsOrFirst as string,
            };
        }

        const consentId = params.consentId;

        if (typeof consentId === 'undefined' || consentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "consentId"',
            );
        }
        const apiPath = '/account/consents/{consentId}'.replace(
            '{consentId}',
            encodeURIComponent(String(consentId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get a list of the token families issued under an OAuth2 consent. Each entry represents one authorized device or session; the token secrets themselves are never returned.
     *
     * @param {string} params.consentId - Consent unique ID.
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2ConsentTokenList>}
     */
    listConsentTokens(params: {
        consentId: string;
        queries?: string[];
        total?: boolean;
    }): Promise<Models.Oauth2ConsentTokenList>;
    /**
     * Get a list of the token families issued under an OAuth2 consent. Each entry represents one authorized device or session; the token secrets themselves are never returned.
     *
     * @param {string} consentId - Consent unique ID.
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2ConsentTokenList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listConsentTokens(
        consentId: string,
        queries?: string[],
        total?: boolean,
    ): Promise<Models.Oauth2ConsentTokenList>;
    listConsentTokens(
        paramsOrFirst:
            { consentId: string; queries?: string[]; total?: boolean } | string,
        ...rest: [string[]?, boolean?]
    ): Promise<Models.Oauth2ConsentTokenList> {
        let params: { consentId: string; queries?: string[]; total?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                consentId: string;
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                consentId: paramsOrFirst as string,
                queries: rest[0] as string[],
                total: rest[1] as boolean,
            };
        }

        const consentId = params.consentId;
        const queries = params.queries;
        const total = params.total;

        if (typeof consentId === 'undefined' || consentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "consentId"',
            );
        }
        const apiPath = '/account/consents/{consentId}/tokens'.replace(
            '{consentId}',
            encodeURIComponent(String(consentId)),
        );
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oauth2ConsentTokenList',
            models: {
                oauth2ConsentTokenList: {
                    defaults: {
                        total: 0,
                        tokens: [],
                    },
                    properties: {
                        tokens: {
                            items: 'oauth2ConsentToken',
                        },
                    },
                },
                oauth2ConsentToken: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        consentId: '',
                        userId: '',
                        appId: '',
                        cimdUrl: '',
                        scopes: [],
                        resources: [],
                        authorizationDetails: '[]',
                        expire: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a token family issued under an OAuth2 consent by its unique ID. The token secrets themselves are never returned.
     *
     * @param {string} params.consentId - Consent unique ID.
     * @param {string} params.tokenId - Token unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2ConsentToken>}
     */
    getConsentToken(params: {
        consentId: string;
        tokenId: string;
    }): Promise<Models.Oauth2ConsentToken>;
    /**
     * Get a token family issued under an OAuth2 consent by its unique ID. The token secrets themselves are never returned.
     *
     * @param {string} consentId - Consent unique ID.
     * @param {string} tokenId - Token unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Oauth2ConsentToken>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getConsentToken(
        consentId: string,
        tokenId: string,
    ): Promise<Models.Oauth2ConsentToken>;
    getConsentToken(
        paramsOrFirst: { consentId: string; tokenId: string } | string,
        ...rest: [string?]
    ): Promise<Models.Oauth2ConsentToken> {
        let params: { consentId: string; tokenId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                consentId: string;
                tokenId: string;
            };
        } else {
            params = {
                consentId: paramsOrFirst as string,
                tokenId: rest[0] as string,
            };
        }

        const consentId = params.consentId;
        const tokenId = params.tokenId;

        if (typeof consentId === 'undefined' || consentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "consentId"',
            );
        }
        if (typeof tokenId === 'undefined' || tokenId === '') {
            throw new AppwriteException(
                'Missing required parameter: "tokenId"',
            );
        }
        const apiPath = '/account/consents/{consentId}/tokens/{tokenId}'
            .replace('{consentId}', encodeURIComponent(String(consentId)))
            .replace('{tokenId}', encodeURIComponent(String(tokenId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'oauth2ConsentToken',
            models: {
                oauth2ConsentToken: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        consentId: '',
                        userId: '',
                        appId: '',
                        cimdUrl: '',
                        scopes: [],
                        resources: [],
                        authorizationDetails: '[]',
                        expire: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a token family issued under an OAuth2 consent by its unique ID. The access and refresh tokens of the family stop working immediately; other token families and the consent itself are unaffected.
     *
     * @param {string} params.consentId - Consent unique ID.
     * @param {string} params.tokenId - Token unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteConsentToken(params: {
        consentId: string;
        tokenId: string;
    }): Promise<{}>;
    /**
     * Delete a token family issued under an OAuth2 consent by its unique ID. The access and refresh tokens of the family stop working immediately; other token families and the consent itself are unaffected.
     *
     * @param {string} consentId - Consent unique ID.
     * @param {string} tokenId - Token unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteConsentToken(consentId: string, tokenId: string): Promise<{}>;
    deleteConsentToken(
        paramsOrFirst: { consentId: string; tokenId: string } | string,
        ...rest: [string?]
    ): Promise<{}> {
        let params: { consentId: string; tokenId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                consentId: string;
                tokenId: string;
            };
        } else {
            params = {
                consentId: paramsOrFirst as string,
                tokenId: rest[0] as string,
            };
        }

        const consentId = params.consentId;
        const tokenId = params.tokenId;

        if (typeof consentId === 'undefined' || consentId === '') {
            throw new AppwriteException(
                'Missing required parameter: "consentId"',
            );
        }
        if (typeof tokenId === 'undefined' || tokenId === '') {
            throw new AppwriteException(
                'Missing required parameter: "tokenId"',
            );
        }
        const apiPath = '/account/consents/{consentId}/tokens/{tokenId}'
            .replace('{consentId}', encodeURIComponent(String(consentId)))
            .replace('{tokenId}', encodeURIComponent(String(tokenId)));
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Get coupon details for an account.
     *
     * @param {string} params.couponId - ID of the coupon
     * @throws {AppwriteException}
     * @returns {Promise<Models.Coupon>}
     */
    getCoupon(params: { couponId: string }): Promise<Models.Coupon>;
    /**
     * Get coupon details for an account.
     *
     * @param {string} couponId - ID of the coupon
     * @throws {AppwriteException}
     * @returns {Promise<Models.Coupon>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getCoupon(couponId: string): Promise<Models.Coupon>;
    getCoupon(
        paramsOrFirst: { couponId: string } | string,
    ): Promise<Models.Coupon> {
        let params: { couponId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { couponId: string };
        } else {
            params = {
                couponId: paramsOrFirst as string,
            };
        }

        const couponId = params.couponId;

        if (typeof couponId === 'undefined' || couponId === '') {
            throw new AppwriteException(
                'Missing required parameter: "couponId"',
            );
        }
        const apiPath = '/account/coupons/{couponId}'.replace(
            '{couponId}',
            encodeURIComponent(String(couponId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'coupon',
            models: {
                coupon: {
                    defaults: {
                        $id: '',
                        code: '',
                        credits: 0,
                        expiration: '',
                        campaign: '',
                        status: '',
                        onlyNewOrgs: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update currently logged in user account email address. After changing user address, the user confirmation status will get reset. A new confirmation email is not sent automatically however you can use the send confirmation email endpoint again to send the confirmation email. For security measures, user password is required to complete this request.
     * This endpoint can also be used to convert an anonymous account to a normal one, by passing an email address and a new password.
     *
     *
     * @param {string} params.email - User email.
     * @param {string} params.password - User password. Must be at least 8 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateEmail<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        email: string;
        password: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update currently logged in user account email address. After changing user address, the user confirmation status will get reset. A new confirmation email is not sent automatically however you can use the send confirmation email endpoint again to send the confirmation email. For security measures, user password is required to complete this request.
     * This endpoint can also be used to convert an anonymous account to a normal one, by passing an email address and a new password.
     *
     *
     * @param {string} email - User email.
     * @param {string} password - User password. Must be at least 8 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateEmail<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(email: string, password: string): Promise<Models.User<Preferences>>;
    updateEmail<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { email: string; password: string } | string,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { email: string; password: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                email: string;
                password: string;
            };
        } else {
            params = {
                email: paramsOrFirst as string,
                password: rest[0] as string,
            };
        }

        const email = params.email;
        const password = params.password;

        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/account/email';
        const apiPayload: Payload = {};
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the list of identities for the currently logged in user.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, provider, providerUid, providerEmail, providerAccessTokenExpiry
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.IdentityList>}
     */
    listIdentities(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.IdentityList>;
    /**
     * Get the list of identities for the currently logged in user.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, provider, providerUid, providerEmail, providerAccessTokenExpiry
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.IdentityList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listIdentities(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.IdentityList>;
    listIdentities(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.IdentityList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/account/identities';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'identityList',
            models: {
                identityList: {
                    defaults: {
                        total: 0,
                        identities: [],
                    },
                    properties: {
                        identities: {
                            items: 'identity',
                        },
                    },
                },
                identity: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        provider: '',
                        providerUid: '',
                        providerEmail: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        providerIdToken: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete an identity by its unique ID.
     *
     * @param {string} params.identityId - Identity ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteIdentity(params: { identityId: string }): Promise<{}>;
    /**
     * Delete an identity by its unique ID.
     *
     * @param {string} identityId - Identity ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteIdentity(identityId: string): Promise<{}>;
    deleteIdentity(
        paramsOrFirst: { identityId: string } | string,
    ): Promise<{}> {
        let params: { identityId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { identityId: string };
        } else {
            params = {
                identityId: paramsOrFirst as string,
            };
        }

        const identityId = params.identityId;

        if (typeof identityId === 'undefined' || identityId === '') {
            throw new AppwriteException(
                'Missing required parameter: "identityId"',
            );
        }
        const apiPath = '/account/identities/{identityId}'.replace(
            '{identityId}',
            encodeURIComponent(String(identityId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * List all invoices tied to an account.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: teamId, aggregationId, type, amount, currency, from, to, dueAt, attempts, status, grossAmount
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.InvoiceList>}
     */
    listInvoices(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.InvoiceList>;
    /**
     * List all invoices tied to an account.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: teamId, aggregationId, type, amount, currency, from, to, dueAt, attempts, status, grossAmount
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.InvoiceList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listInvoices(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.InvoiceList>;
    listInvoices(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.InvoiceList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/account/invoices';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'invoiceList',
            models: {
                invoiceList: {
                    defaults: {
                        total: 0,
                        invoices: [],
                    },
                    properties: {
                        invoices: {
                            items: 'invoice',
                        },
                    },
                },
                invoice: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        teamId: '',
                        aggregationId: '',
                        plan: 'tier-0',
                        usage: [],
                        amount: 0,
                        tax: 0,
                        taxAmount: 0,
                        vat: 0,
                        vatAmount: 0,
                        grossAmount: 0,
                        creditsUsed: 0,
                        currency: 'USD',
                        status: '',
                        type: 'subscription',
                        from: '',
                        to: '',
                    },
                    properties: {
                        usage: {
                            items: 'usageResources',
                        },
                    },
                },
                usageResources: {
                    defaults: {
                        name: '',
                        value: 0,
                        amount: 0,
                        rate: 0,
                        desc: '',
                        resourceId: '',
                        type: '',
                        specification: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to create a JSON Web Token. You can use the resulting JWT to authenticate on behalf of the current user when working with the Appwrite server-side API and SDKs. The JWT secret is valid for 15 minutes from its creation and will be invalid if the user will logout in that time frame.
     *
     * @param {number} params.duration - Time in seconds before JWT expires. Default duration is 900 seconds, and maximum is 3600 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Jwt>}
     */
    createJWT(params?: { duration?: number }): Promise<Models.Jwt>;
    /**
     * Use this endpoint to create a JSON Web Token. You can use the resulting JWT to authenticate on behalf of the current user when working with the Appwrite server-side API and SDKs. The JWT secret is valid for 15 minutes from its creation and will be invalid if the user will logout in that time frame.
     *
     * @param {number} duration - Time in seconds before JWT expires. Default duration is 900 seconds, and maximum is 3600 seconds.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Jwt>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createJWT(duration?: number): Promise<Models.Jwt>;
    createJWT(
        paramsOrFirst?: { duration?: number } | number,
    ): Promise<Models.Jwt> {
        let params: { duration?: number };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { duration?: number };
        } else {
            params = {
                duration: paramsOrFirst as number,
            };
        }

        const duration = params.duration;

        const apiPath = '/account/jwts';
        const apiPayload: Payload = {};
        if (typeof duration !== 'undefined') {
            apiPayload['duration'] = duration;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'jwt',
            models: {
                jwt: {
                    defaults: {
                        jwt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a list of all API keys from the current account.
     *
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.KeyList>}
     */
    listKeys(params?: { total?: boolean }): Promise<Models.KeyList>;
    /**
     * Get a list of all API keys from the current account.
     *
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.KeyList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listKeys(total?: boolean): Promise<Models.KeyList>;
    listKeys(
        paramsOrFirst?: { total?: boolean } | boolean,
    ): Promise<Models.KeyList> {
        let params: { total?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { total?: boolean };
        } else {
            params = {
                total: paramsOrFirst as boolean,
            };
        }

        const total = params.total;

        const apiPath = '/account/keys';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'keyList',
            models: {
                keyList: {
                    defaults: {
                        total: 0,
                        keys: [],
                    },
                    properties: {
                        keys: {
                            items: 'key',
                        },
                    },
                },
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new account API key.
     *
     * @param {string} params.name - Key name. Max length: 128 chars.
     * @param {AccountKeyScopes[]} params.scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} params.expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     */
    createKey(params: {
        name: string;
        scopes: AccountKeyScopes[];
        expire?: string | null;
    }): Promise<Models.Key>;
    /**
     * Create a new account API key.
     *
     * @param {string} name - Key name. Max length: 128 chars.
     * @param {AccountKeyScopes[]} scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createKey(
        name: string,
        scopes: AccountKeyScopes[],
        expire?: string | null,
    ): Promise<Models.Key>;
    createKey(
        paramsOrFirst:
            | {
                  name: string;
                  scopes: AccountKeyScopes[];
                  expire?: string | null;
              }
            | string,
        ...rest: [AccountKeyScopes[]?, (string | null)?]
    ): Promise<Models.Key> {
        let params: {
            name: string;
            scopes: AccountKeyScopes[];
            expire?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                name: string;
                scopes: AccountKeyScopes[];
                expire?: string | null;
            };
        } else {
            params = {
                name: paramsOrFirst as string,
                scopes: rest[0] as AccountKeyScopes[],
                expire: rest[1] as string | null,
            };
        }

        const name = params.name;
        const scopes = params.scopes;
        const expire = params.expire;

        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof scopes === 'undefined') {
            throw new AppwriteException('Missing required parameter: "scopes"');
        }
        const apiPath = '/account/keys';
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof expire !== 'undefined') {
            apiPayload['expire'] = expire;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'key',
            models: {
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a key by its unique ID. This endpoint returns details about a specific API key in your account including it's scopes.
     *
     * @param {string} params.keyId - Key unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     */
    getKey(params: { keyId: string }): Promise<Models.Key>;
    /**
     * Get a key by its unique ID. This endpoint returns details about a specific API key in your account including it's scopes.
     *
     * @param {string} keyId - Key unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getKey(keyId: string): Promise<Models.Key>;
    getKey(paramsOrFirst: { keyId: string } | string): Promise<Models.Key> {
        let params: { keyId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { keyId: string };
        } else {
            params = {
                keyId: paramsOrFirst as string,
            };
        }

        const keyId = params.keyId;

        if (typeof keyId === 'undefined' || keyId === '') {
            throw new AppwriteException('Missing required parameter: "keyId"');
        }
        const apiPath = '/account/keys/{keyId}'.replace(
            '{keyId}',
            encodeURIComponent(String(keyId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'key',
            models: {
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a key by its unique ID. Use this endpoint to update the name, scopes, or expiration time of an API key.
     *
     * @param {string} params.keyId - Key unique ID.
     * @param {string} params.name - Key name. Max length: 128 chars.
     * @param {AccountKeyScopes[]} params.scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} params.expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     */
    updateKey(params: {
        keyId: string;
        name: string;
        scopes: AccountKeyScopes[];
        expire?: string | null;
    }): Promise<Models.Key>;
    /**
     * Update a key by its unique ID. Use this endpoint to update the name, scopes, or expiration time of an API key.
     *
     * @param {string} keyId - Key unique ID.
     * @param {string} name - Key name. Max length: 128 chars.
     * @param {AccountKeyScopes[]} scopes - Key scopes list. Maximum of 200 scopes are allowed.
     * @param {string | null} expire - Expiration time in [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format. Use null for unlimited expiration.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Key>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateKey(
        keyId: string,
        name: string,
        scopes: AccountKeyScopes[],
        expire?: string | null,
    ): Promise<Models.Key>;
    updateKey(
        paramsOrFirst:
            | {
                  keyId: string;
                  name: string;
                  scopes: AccountKeyScopes[];
                  expire?: string | null;
              }
            | string,
        ...rest: [string?, AccountKeyScopes[]?, (string | null)?]
    ): Promise<Models.Key> {
        let params: {
            keyId: string;
            name: string;
            scopes: AccountKeyScopes[];
            expire?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                keyId: string;
                name: string;
                scopes: AccountKeyScopes[];
                expire?: string | null;
            };
        } else {
            params = {
                keyId: paramsOrFirst as string,
                name: rest[0] as string,
                scopes: rest[1] as AccountKeyScopes[],
                expire: rest[2] as string | null,
            };
        }

        const keyId = params.keyId;
        const name = params.name;
        const scopes = params.scopes;
        const expire = params.expire;

        if (typeof keyId === 'undefined' || keyId === '') {
            throw new AppwriteException('Missing required parameter: "keyId"');
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        if (typeof scopes === 'undefined') {
            throw new AppwriteException('Missing required parameter: "scopes"');
        }
        const apiPath = '/account/keys/{keyId}'.replace(
            '{keyId}',
            encodeURIComponent(String(keyId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof expire !== 'undefined') {
            apiPayload['expire'] = expire;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'key',
            models: {
                key: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        description: '',
                        expire: '',
                        scopes: [],
                        secret: '',
                        accessedAt: '',
                        sdks: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a key by its unique ID. Once deleted, the key can no longer be used to authenticate API calls.
     *
     * @param {string} params.keyId - Key unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteKey(params: { keyId: string }): Promise<{}>;
    /**
     * Delete a key by its unique ID. Once deleted, the key can no longer be used to authenticate API calls.
     *
     * @param {string} keyId - Key unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteKey(keyId: string): Promise<{}>;
    deleteKey(paramsOrFirst: { keyId: string } | string): Promise<{}> {
        let params: { keyId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { keyId: string };
        } else {
            params = {
                keyId: paramsOrFirst as string,
            };
        }

        const keyId = params.keyId;

        if (typeof keyId === 'undefined' || keyId === '') {
            throw new AppwriteException('Missing required parameter: "keyId"');
        }
        const apiPath = '/account/keys/{keyId}'.replace(
            '{keyId}',
            encodeURIComponent(String(keyId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Enable or disable MFA on an account.
     *
     * @param {boolean} params.mfa - Enable or disable MFA.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateMFA<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: { mfa: boolean }): Promise<Models.User<Preferences>>;
    /**
     * Enable or disable MFA on an account.
     *
     * @param {boolean} mfa - Enable or disable MFA.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMFA<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(mfa: boolean): Promise<Models.User<Preferences>>;
    updateMFA<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { mfa: boolean } | boolean,
    ): Promise<Models.User<Preferences>> {
        let params: { mfa: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { mfa: boolean };
        } else {
            params = {
                mfa: paramsOrFirst as boolean,
            };
        }

        const mfa = params.mfa;

        if (typeof mfa === 'undefined') {
            throw new AppwriteException('Missing required parameter: "mfa"');
        }
        const apiPath = '/account/mfa';
        const apiPayload: Payload = {};
        if (typeof mfa !== 'undefined') {
            apiPayload['mfa'] = mfa;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add an authenticator app to be used as an MFA factor. Verify the authenticator using the [verify authenticator](/docs/references/cloud/client-web/account#updateMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} params.type - Type of authenticator. Must be `totp`
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaType>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.createMFAAuthenticator` instead.
     */
    createMfaAuthenticator(params: {
        type: AuthenticatorType;
    }): Promise<Models.MfaType>;
    /**
     * Add an authenticator app to be used as an MFA factor. Verify the authenticator using the [verify authenticator](/docs/references/cloud/client-web/account#updateMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} type - Type of authenticator. Must be `totp`
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaType>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMfaAuthenticator(type: AuthenticatorType): Promise<Models.MfaType>;
    createMfaAuthenticator(
        paramsOrFirst: { type: AuthenticatorType } | AuthenticatorType,
    ): Promise<Models.MfaType> {
        let params: { type: AuthenticatorType };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'type' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { type: AuthenticatorType };
        } else {
            params = {
                type: paramsOrFirst as AuthenticatorType,
            };
        }

        const type = params.type;

        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/account/mfa/authenticators/{type}'.replace(
            '{type}',
            encodeURIComponent(String(type)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaType',
            models: {
                mfaType: {
                    defaults: {
                        secret: '',
                        uri: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Add an authenticator app to be used as an MFA factor. Verify the authenticator using the [verify authenticator](/docs/references/cloud/client-web/account#updateMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} params.type - Type of authenticator. Must be `totp`
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaType>}
     */
    createMFAAuthenticator(params: {
        type: AuthenticatorType;
    }): Promise<Models.MfaType>;
    /**
     * Add an authenticator app to be used as an MFA factor. Verify the authenticator using the [verify authenticator](/docs/references/cloud/client-web/account#updateMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} type - Type of authenticator. Must be `totp`
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaType>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMFAAuthenticator(type: AuthenticatorType): Promise<Models.MfaType>;
    createMFAAuthenticator(
        paramsOrFirst: { type: AuthenticatorType } | AuthenticatorType,
    ): Promise<Models.MfaType> {
        let params: { type: AuthenticatorType };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'type' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { type: AuthenticatorType };
        } else {
            params = {
                type: paramsOrFirst as AuthenticatorType,
            };
        }

        const type = params.type;

        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/account/mfa/authenticators/{type}'.replace(
            '{type}',
            encodeURIComponent(String(type)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaType',
            models: {
                mfaType: {
                    defaults: {
                        secret: '',
                        uri: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Verify an authenticator app after adding it using the [add authenticator](/docs/references/cloud/client-web/account#createMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} params.type - Type of authenticator.
     * @param {string} params.otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.updateMFAAuthenticator` instead.
     */
    updateMfaAuthenticator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        type: AuthenticatorType;
        otp: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Verify an authenticator app after adding it using the [add authenticator](/docs/references/cloud/client-web/account#createMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} type - Type of authenticator.
     * @param {string} otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMfaAuthenticator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(type: AuthenticatorType, otp: string): Promise<Models.User<Preferences>>;
    updateMfaAuthenticator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            { type: AuthenticatorType; otp: string } | AuthenticatorType,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { type: AuthenticatorType; otp: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('type' in paramsOrFirst || 'otp' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                type: AuthenticatorType;
                otp: string;
            };
        } else {
            params = {
                type: paramsOrFirst as AuthenticatorType,
                otp: rest[0] as string,
            };
        }

        const type = params.type;
        const otp = params.otp;

        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        if (typeof otp === 'undefined') {
            throw new AppwriteException('Missing required parameter: "otp"');
        }
        const apiPath = '/account/mfa/authenticators/{type}'.replace(
            '{type}',
            encodeURIComponent(String(type)),
        );
        const apiPayload: Payload = {};
        if (typeof otp !== 'undefined') {
            apiPayload['otp'] = otp;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Verify an authenticator app after adding it using the [add authenticator](/docs/references/cloud/client-web/account#createMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} params.type - Type of authenticator.
     * @param {string} params.otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateMFAAuthenticator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        type: AuthenticatorType;
        otp: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Verify an authenticator app after adding it using the [add authenticator](/docs/references/cloud/client-web/account#createMfaAuthenticator) method.
     *
     * @param {AuthenticatorType} type - Type of authenticator.
     * @param {string} otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMFAAuthenticator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(type: AuthenticatorType, otp: string): Promise<Models.User<Preferences>>;
    updateMFAAuthenticator<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst:
            { type: AuthenticatorType; otp: string } | AuthenticatorType,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { type: AuthenticatorType; otp: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('type' in paramsOrFirst || 'otp' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                type: AuthenticatorType;
                otp: string;
            };
        } else {
            params = {
                type: paramsOrFirst as AuthenticatorType,
                otp: rest[0] as string,
            };
        }

        const type = params.type;
        const otp = params.otp;

        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        if (typeof otp === 'undefined') {
            throw new AppwriteException('Missing required parameter: "otp"');
        }
        const apiPath = '/account/mfa/authenticators/{type}'.replace(
            '{type}',
            encodeURIComponent(String(type)),
        );
        const apiPayload: Payload = {};
        if (typeof otp !== 'undefined') {
            apiPayload['otp'] = otp;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete an authenticator for a user by ID.
     *
     * @param {AuthenticatorType} params.type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.deleteMFAAuthenticator` instead.
     */
    deleteMfaAuthenticator(params: { type: AuthenticatorType }): Promise<{}>;
    /**
     * Delete an authenticator for a user by ID.
     *
     * @param {AuthenticatorType} type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteMfaAuthenticator(type: AuthenticatorType): Promise<{}>;
    deleteMfaAuthenticator(
        paramsOrFirst: { type: AuthenticatorType } | AuthenticatorType,
    ): Promise<{}> {
        let params: { type: AuthenticatorType };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'type' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { type: AuthenticatorType };
        } else {
            params = {
                type: paramsOrFirst as AuthenticatorType,
            };
        }

        const type = params.type;

        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/account/mfa/authenticators/{type}'.replace(
            '{type}',
            encodeURIComponent(String(type)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Delete an authenticator for a user by ID.
     *
     * @param {AuthenticatorType} params.type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteMFAAuthenticator(params: { type: AuthenticatorType }): Promise<{}>;
    /**
     * Delete an authenticator for a user by ID.
     *
     * @param {AuthenticatorType} type - Type of authenticator.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteMFAAuthenticator(type: AuthenticatorType): Promise<{}>;
    deleteMFAAuthenticator(
        paramsOrFirst: { type: AuthenticatorType } | AuthenticatorType,
    ): Promise<{}> {
        let params: { type: AuthenticatorType };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'type' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { type: AuthenticatorType };
        } else {
            params = {
                type: paramsOrFirst as AuthenticatorType,
            };
        }

        const type = params.type;

        if (typeof type === 'undefined') {
            throw new AppwriteException('Missing required parameter: "type"');
        }
        const apiPath = '/account/mfa/authenticators/{type}'.replace(
            '{type}',
            encodeURIComponent(String(type)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Begin the process of MFA verification after sign-in. Finish the flow with [updateMfaChallenge](/docs/references/cloud/client-web/account#updateMfaChallenge) method.
     *
     * @param {AuthenticationFactor} params.factor - Factor used for verification. Must be one of following: `email`, `phone`, `totp`, `recoveryCode`, `custom`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaChallenge>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.createMFAChallenge` instead.
     */
    createMfaChallenge(params: {
        factor: AuthenticationFactor;
    }): Promise<Models.MfaChallenge>;
    /**
     * Begin the process of MFA verification after sign-in. Finish the flow with [updateMfaChallenge](/docs/references/cloud/client-web/account#updateMfaChallenge) method.
     *
     * @param {AuthenticationFactor} factor - Factor used for verification. Must be one of following: `email`, `phone`, `totp`, `recoveryCode`, `custom`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaChallenge>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMfaChallenge(
        factor: AuthenticationFactor,
    ): Promise<Models.MfaChallenge>;
    createMfaChallenge(
        paramsOrFirst: { factor: AuthenticationFactor } | AuthenticationFactor,
    ): Promise<Models.MfaChallenge> {
        let params: { factor: AuthenticationFactor };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'factor' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { factor: AuthenticationFactor };
        } else {
            params = {
                factor: paramsOrFirst as AuthenticationFactor,
            };
        }

        const factor = params.factor;

        if (typeof factor === 'undefined') {
            throw new AppwriteException('Missing required parameter: "factor"');
        }
        const apiPath = '/account/mfa/challenges';
        const apiPayload: Payload = {};
        if (typeof factor !== 'undefined') {
            apiPayload['factor'] = factor;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaChallenge',
            models: {
                mfaChallenge: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        expire: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Begin the process of MFA verification after sign-in. Finish the flow with [updateMfaChallenge](/docs/references/cloud/client-web/account#updateMfaChallenge) method.
     *
     * @param {AuthenticationFactor} params.factor - Factor used for verification. Must be one of following: `email`, `phone`, `totp`, `recoveryCode`, `custom`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaChallenge>}
     */
    createMFAChallenge(params: {
        factor: AuthenticationFactor;
    }): Promise<Models.MfaChallenge>;
    /**
     * Begin the process of MFA verification after sign-in. Finish the flow with [updateMfaChallenge](/docs/references/cloud/client-web/account#updateMfaChallenge) method.
     *
     * @param {AuthenticationFactor} factor - Factor used for verification. Must be one of following: `email`, `phone`, `totp`, `recoveryCode`, `custom`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaChallenge>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMFAChallenge(
        factor: AuthenticationFactor,
    ): Promise<Models.MfaChallenge>;
    createMFAChallenge(
        paramsOrFirst: { factor: AuthenticationFactor } | AuthenticationFactor,
    ): Promise<Models.MfaChallenge> {
        let params: { factor: AuthenticationFactor };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'factor' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { factor: AuthenticationFactor };
        } else {
            params = {
                factor: paramsOrFirst as AuthenticationFactor,
            };
        }

        const factor = params.factor;

        if (typeof factor === 'undefined') {
            throw new AppwriteException('Missing required parameter: "factor"');
        }
        const apiPath = '/account/mfa/challenges';
        const apiPayload: Payload = {};
        if (typeof factor !== 'undefined') {
            apiPayload['factor'] = factor;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaChallenge',
            models: {
                mfaChallenge: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        expire: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Complete the MFA challenge by providing the one-time password. Finish the process of MFA verification by providing the one-time password. To begin the flow, use [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method.
     *
     * @param {string} params.challengeId - ID of the challenge.
     * @param {string} params.otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.updateMFAChallenge` instead.
     */
    updateMfaChallenge(params: {
        challengeId: string;
        otp: string;
    }): Promise<Models.Session>;
    /**
     * Complete the MFA challenge by providing the one-time password. Finish the process of MFA verification by providing the one-time password. To begin the flow, use [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method.
     *
     * @param {string} challengeId - ID of the challenge.
     * @param {string} otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMfaChallenge(
        challengeId: string,
        otp: string,
    ): Promise<Models.Session>;
    updateMfaChallenge(
        paramsOrFirst: { challengeId: string; otp: string } | string,
        ...rest: [string?]
    ): Promise<Models.Session> {
        let params: { challengeId: string; otp: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                challengeId: string;
                otp: string;
            };
        } else {
            params = {
                challengeId: paramsOrFirst as string,
                otp: rest[0] as string,
            };
        }

        const challengeId = params.challengeId;
        const otp = params.otp;

        if (typeof challengeId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "challengeId"',
            );
        }
        if (typeof otp === 'undefined') {
            throw new AppwriteException('Missing required parameter: "otp"');
        }
        const apiPath = '/account/mfa/challenges';
        const apiPayload: Payload = {};
        if (typeof challengeId !== 'undefined') {
            apiPayload['challengeId'] = challengeId;
        }
        if (typeof otp !== 'undefined') {
            apiPayload['otp'] = otp;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Complete the MFA challenge by providing the one-time password. Finish the process of MFA verification by providing the one-time password. To begin the flow, use [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method.
     *
     * @param {string} params.challengeId - ID of the challenge.
     * @param {string} params.otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    updateMFAChallenge(params: {
        challengeId: string;
        otp: string;
    }): Promise<Models.Session>;
    /**
     * Complete the MFA challenge by providing the one-time password. Finish the process of MFA verification by providing the one-time password. To begin the flow, use [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method.
     *
     * @param {string} challengeId - ID of the challenge.
     * @param {string} otp - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMFAChallenge(
        challengeId: string,
        otp: string,
    ): Promise<Models.Session>;
    updateMFAChallenge(
        paramsOrFirst: { challengeId: string; otp: string } | string,
        ...rest: [string?]
    ): Promise<Models.Session> {
        let params: { challengeId: string; otp: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                challengeId: string;
                otp: string;
            };
        } else {
            params = {
                challengeId: paramsOrFirst as string,
                otp: rest[0] as string,
            };
        }

        const challengeId = params.challengeId;
        const otp = params.otp;

        if (typeof challengeId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "challengeId"',
            );
        }
        if (typeof otp === 'undefined') {
            throw new AppwriteException('Missing required parameter: "otp"');
        }
        const apiPath = '/account/mfa/challenges';
        const apiPayload: Payload = {};
        if (typeof challengeId !== 'undefined') {
            apiPayload['challengeId'] = challengeId;
        }
        if (typeof otp !== 'undefined') {
            apiPayload['otp'] = otp;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List the factors available on the account to be used as a MFA challange.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaFactors>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.listMFAFactors` instead.
     */
    listMfaFactors(): Promise<Models.MfaFactors> {
        const apiPath = '/account/mfa/factors';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaFactors',
            models: {
                mfaFactors: {
                    defaults: {
                        totp: false,
                        phone: false,
                        email: false,
                        recoveryCode: false,
                        custom: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List the factors available on the account to be used as a MFA challange.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaFactors>}
     */
    listMFAFactors(): Promise<Models.MfaFactors> {
        const apiPath = '/account/mfa/factors';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaFactors',
            models: {
                mfaFactors: {
                    defaults: {
                        totp: false,
                        phone: false,
                        email: false,
                        recoveryCode: false,
                        custom: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get recovery codes that can be used as backup for MFA flow. Before getting codes, they must be generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method. An OTP challenge is required to read recovery codes.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.getMFARecoveryCodes` instead.
     */
    getMfaRecoveryCodes(): Promise<Models.MfaRecoveryCodes> {
        const apiPath = '/account/mfa/recovery-codes';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaRecoveryCodes',
            models: {
                mfaRecoveryCodes: {
                    defaults: {
                        recoveryCodes: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get recovery codes that can be used as backup for MFA flow. Before getting codes, they must be generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method. An OTP challenge is required to read recovery codes.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     */
    getMFARecoveryCodes(): Promise<Models.MfaRecoveryCodes> {
        const apiPath = '/account/mfa/recovery-codes';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaRecoveryCodes',
            models: {
                mfaRecoveryCodes: {
                    defaults: {
                        recoveryCodes: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Generate recovery codes as backup for MFA flow. It's recommended to generate and show then immediately after user successfully adds their authehticator. Recovery codes can be used as a MFA verification type in [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.createMFARecoveryCodes` instead.
     */
    createMfaRecoveryCodes(): Promise<Models.MfaRecoveryCodes> {
        const apiPath = '/account/mfa/recovery-codes';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaRecoveryCodes',
            models: {
                mfaRecoveryCodes: {
                    defaults: {
                        recoveryCodes: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Generate recovery codes as backup for MFA flow. It's recommended to generate and show then immediately after user successfully adds their authehticator. Recovery codes can be used as a MFA verification type in [createMfaChallenge](/docs/references/cloud/client-web/account#createMfaChallenge) method.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     */
    createMFARecoveryCodes(): Promise<Models.MfaRecoveryCodes> {
        const apiPath = '/account/mfa/recovery-codes';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaRecoveryCodes',
            models: {
                mfaRecoveryCodes: {
                    defaults: {
                        recoveryCodes: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Regenerate recovery codes that can be used as backup for MFA flow. Before regenerating codes, they must be first generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method. An OTP challenge is required to regenreate recovery codes.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.updateMFARecoveryCodes` instead.
     */
    updateMfaRecoveryCodes(): Promise<Models.MfaRecoveryCodes> {
        const apiPath = '/account/mfa/recovery-codes';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaRecoveryCodes',
            models: {
                mfaRecoveryCodes: {
                    defaults: {
                        recoveryCodes: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Regenerate recovery codes that can be used as backup for MFA flow. Before regenerating codes, they must be first generated using [createMfaRecoveryCodes](/docs/references/cloud/client-web/account#createMfaRecoveryCodes) method. An OTP challenge is required to regenreate recovery codes.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.MfaRecoveryCodes>}
     */
    updateMFARecoveryCodes(): Promise<Models.MfaRecoveryCodes> {
        const apiPath = '/account/mfa/recovery-codes';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'mfaRecoveryCodes',
            models: {
                mfaRecoveryCodes: {
                    defaults: {
                        recoveryCodes: [],
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update currently logged in user account name.
     *
     * @param {string} params.name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateName<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: { name: string }): Promise<Models.User<Preferences>>;
    /**
     * Update currently logged in user account name.
     *
     * @param {string} name - User name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateName<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(name: string): Promise<Models.User<Preferences>>;
    updateName<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { name: string } | string,
    ): Promise<Models.User<Preferences>> {
        let params: { name: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { name: string };
        } else {
            params = {
                name: paramsOrFirst as string,
            };
        }

        const name = params.name;

        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        const apiPath = '/account/name';
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the list of verified passkeys registered by the currently logged in user.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter and order on the following attributes: $id, $createdAt, $updatedAt, name, accessedAt
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PasskeyList>}
     */
    listPasskeys(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.PasskeyList>;
    /**
     * Get the list of verified passkeys registered by the currently logged in user.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/queries). Maximum of 100 queries are allowed, each 4096 characters long. You may filter and order on the following attributes: $id, $createdAt, $updatedAt, name, accessedAt
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PasskeyList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listPasskeys(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.PasskeyList>;
    listPasskeys(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.PasskeyList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/account/passkeys';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'passkeyList',
            models: {
                passkeyList: {
                    defaults: {
                        total: 0,
                        passkeys: [],
                    },
                    properties: {
                        passkeys: {
                            items: 'passkey',
                        },
                    },
                },
                passkey: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Start registering a passkey for the currently logged in user. The session must have signed in or completed an MFA challenge within the last 10 minutes. Pass the returned `publicKey` options to `navigator.credentials.create()`, then complete the registration with [Update passkey verification](/docs/references/cloud/client-web/account#updatePasskeyVerification). The passkey stays pending until verified, and the challenge expires after 5 minutes.
     *
     * @param {string} params.passkeyId - Passkey ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.name - Passkey name, shown when listing passkeys. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PasskeyChallenge>}
     */
    createPasskey(params?: {
        passkeyId?: string;
        name?: string;
    }): Promise<Models.PasskeyChallenge>;
    /**
     * Start registering a passkey for the currently logged in user. The session must have signed in or completed an MFA challenge within the last 10 minutes. Pass the returned `publicKey` options to `navigator.credentials.create()`, then complete the registration with [Update passkey verification](/docs/references/cloud/client-web/account#updatePasskeyVerification). The passkey stays pending until verified, and the challenge expires after 5 minutes.
     *
     * @param {string} passkeyId - Passkey ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} name - Passkey name, shown when listing passkeys. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PasskeyChallenge>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPasskey(
        passkeyId?: string,
        name?: string,
    ): Promise<Models.PasskeyChallenge>;
    createPasskey(
        paramsOrFirst?: { passkeyId?: string; name?: string } | string,
        ...rest: [string?]
    ): Promise<Models.PasskeyChallenge> {
        let params: { passkeyId?: string; name?: string };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                passkeyId?: string;
                name?: string;
            };
        } else {
            params = {
                passkeyId: paramsOrFirst as string,
                name: rest[0] as string,
            };
        }

        const passkeyId = params.passkeyId;
        const name = params.name;

        const apiPath = '/account/passkeys';
        const apiPayload: Payload = {};
        if (typeof passkeyId !== 'undefined') {
            apiPayload['passkeyId'] = passkeyId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'passkeyChallenge',
            models: {
                passkeyChallenge: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        passkeyId: '',
                        expire: '',
                        publicKey: {},
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a verified passkey of the currently logged in user by its unique ID.
     *
     * @param {string} params.passkeyId - Passkey ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Passkey>}
     */
    getPasskey(params: { passkeyId: string }): Promise<Models.Passkey>;
    /**
     * Get a verified passkey of the currently logged in user by its unique ID.
     *
     * @param {string} passkeyId - Passkey ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Passkey>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPasskey(passkeyId: string): Promise<Models.Passkey>;
    getPasskey(
        paramsOrFirst: { passkeyId: string } | string,
    ): Promise<Models.Passkey> {
        let params: { passkeyId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { passkeyId: string };
        } else {
            params = {
                passkeyId: paramsOrFirst as string,
            };
        }

        const passkeyId = params.passkeyId;

        if (typeof passkeyId === 'undefined' || passkeyId === '') {
            throw new AppwriteException(
                'Missing required parameter: "passkeyId"',
            );
        }
        const apiPath = '/account/passkeys/{passkeyId}'.replace(
            '{passkeyId}',
            encodeURIComponent(String(passkeyId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'passkey',
            models: {
                passkey: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Rename a passkey of the currently logged in user.
     *
     * @param {string} params.passkeyId - Passkey ID.
     * @param {string} params.name - Passkey name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Passkey>}
     */
    updatePasskey(params: {
        passkeyId: string;
        name: string;
    }): Promise<Models.Passkey>;
    /**
     * Rename a passkey of the currently logged in user.
     *
     * @param {string} passkeyId - Passkey ID.
     * @param {string} name - Passkey name. Max length: 128 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Passkey>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasskey(passkeyId: string, name: string): Promise<Models.Passkey>;
    updatePasskey(
        paramsOrFirst: { passkeyId: string; name: string } | string,
        ...rest: [string?]
    ): Promise<Models.Passkey> {
        let params: { passkeyId: string; name: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                passkeyId: string;
                name: string;
            };
        } else {
            params = {
                passkeyId: paramsOrFirst as string,
                name: rest[0] as string,
            };
        }

        const passkeyId = params.passkeyId;
        const name = params.name;

        if (typeof passkeyId === 'undefined' || passkeyId === '') {
            throw new AppwriteException(
                'Missing required parameter: "passkeyId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        const apiPath = '/account/passkeys/{passkeyId}'.replace(
            '{passkeyId}',
            encodeURIComponent(String(passkeyId)),
        );
        const apiPayload: Payload = {};
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'passkey',
            models: {
                passkey: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a passkey from the currently logged in user. The passkey can no longer be used to sign in, although it may remain stored on the user's device. The session must have signed in or completed an MFA challenge within the last 10 minutes.
     *
     * @param {string} params.passkeyId - Passkey ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deletePasskey(params: { passkeyId: string }): Promise<{}>;
    /**
     * Delete a passkey from the currently logged in user. The passkey can no longer be used to sign in, although it may remain stored on the user's device. The session must have signed in or completed an MFA challenge within the last 10 minutes.
     *
     * @param {string} passkeyId - Passkey ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deletePasskey(passkeyId: string): Promise<{}>;
    deletePasskey(paramsOrFirst: { passkeyId: string } | string): Promise<{}> {
        let params: { passkeyId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { passkeyId: string };
        } else {
            params = {
                passkeyId: paramsOrFirst as string,
            };
        }

        const passkeyId = params.passkeyId;

        if (typeof passkeyId === 'undefined' || passkeyId === '') {
            throw new AppwriteException(
                'Missing required parameter: "passkeyId"',
            );
        }
        const apiPath = '/account/passkeys/{passkeyId}'.replace(
            '{passkeyId}',
            encodeURIComponent(String(passkeyId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Complete a passkey registration started with [Create passkey](/docs/references/cloud/client-web/account#createPasskey). Pass the JSON form of the credential returned by `navigator.credentials.create()`, for example `credential.toJSON()`. Each registration can be verified once; a failed attempt requires starting it again.
     *
     * @param {string} params.passkeyId - Passkey ID.
     * @param {object} params.credential - Registration credential returned by the authenticator, in the JSON form produced by `PublicKeyCredential.toJSON()`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Passkey>}
     */
    updatePasskeyVerification(params: {
        passkeyId: string;
        credential: object;
    }): Promise<Models.Passkey>;
    /**
     * Complete a passkey registration started with [Create passkey](/docs/references/cloud/client-web/account#createPasskey). Pass the JSON form of the credential returned by `navigator.credentials.create()`, for example `credential.toJSON()`. Each registration can be verified once; a failed attempt requires starting it again.
     *
     * @param {string} passkeyId - Passkey ID.
     * @param {object} credential - Registration credential returned by the authenticator, in the JSON form produced by `PublicKeyCredential.toJSON()`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Passkey>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasskeyVerification(
        passkeyId: string,
        credential: object,
    ): Promise<Models.Passkey>;
    updatePasskeyVerification(
        paramsOrFirst: { passkeyId: string; credential: object } | string,
        ...rest: [object?]
    ): Promise<Models.Passkey> {
        let params: { passkeyId: string; credential: object };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                passkeyId: string;
                credential: object;
            };
        } else {
            params = {
                passkeyId: paramsOrFirst as string,
                credential: rest[0] as object,
            };
        }

        const passkeyId = params.passkeyId;
        const credential = params.credential;

        if (typeof passkeyId === 'undefined' || passkeyId === '') {
            throw new AppwriteException(
                'Missing required parameter: "passkeyId"',
            );
        }
        if (typeof credential === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "credential"',
            );
        }
        const apiPath = '/account/passkeys/{passkeyId}/verification'.replace(
            '{passkeyId}',
            encodeURIComponent(String(passkeyId)),
        );
        const apiPayload: Payload = {};
        if (typeof credential !== 'undefined') {
            apiPayload['credential'] = credential;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'passkey',
            models: {
                passkey: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        accessedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update currently logged in user password. For validation, user is required to pass in the new password, and the old password. For users created with OAuth, Team Invites and Magic URL, oldPassword is optional.
     *
     * @param {string} params.password - New user password. Must be at least 8 chars.
     * @param {string} params.oldPassword - Current user password. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updatePassword<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        password: string;
        oldPassword?: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update currently logged in user password. For validation, user is required to pass in the new password, and the old password. For users created with OAuth, Team Invites and Magic URL, oldPassword is optional.
     *
     * @param {string} password - New user password. Must be at least 8 chars.
     * @param {string} oldPassword - Current user password. Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePassword<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        password: string,
        oldPassword?: string,
    ): Promise<Models.User<Preferences>>;
    updatePassword<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { password: string; oldPassword?: string } | string,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { password: string; oldPassword?: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                password: string;
                oldPassword?: string;
            };
        } else {
            params = {
                password: paramsOrFirst as string,
                oldPassword: rest[0] as string,
            };
        }

        const password = params.password;
        const oldPassword = params.oldPassword;

        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/account/password';
        const apiPayload: Payload = {};
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof oldPassword !== 'undefined') {
            apiPayload['oldPassword'] = oldPassword;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * List payment methods for this account.
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, expired, failed
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethodList>}
     */
    listPaymentMethods(params?: {
        queries?: string[];
        total?: boolean;
    }): Promise<Models.PaymentMethodList>;
    /**
     * List payment methods for this account.
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK. [Learn more about queries](https://appwrite.io/docs/databases#querying-documents). Maximum of 100 queries are allowed, each 4096 characters long. You may filter on the following attributes: userId, expired, failed
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethodList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listPaymentMethods(
        queries?: string[],
        total?: boolean,
    ): Promise<Models.PaymentMethodList>;
    listPaymentMethods(
        paramsOrFirst?: { queries?: string[]; total?: boolean } | string[],
        ...rest: [boolean?]
    ): Promise<Models.PaymentMethodList> {
        let params: { queries?: string[]; total?: boolean };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
            };
        }

        const queries = params.queries;
        const total = params.total;

        const apiPath = '/account/payment-methods';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'paymentMethodList',
            models: {
                paymentMethodList: {
                    defaults: {
                        total: 0,
                        paymentMethods: [],
                    },
                    properties: {
                        paymentMethods: {
                            items: 'paymentMethod',
                        },
                    },
                },
                paymentMethod: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        providerMethodId: '',
                        clientSecret: '',
                        providerUserId: '',
                        userId: '',
                        default: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create a new payment method for the current user account.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     */
    createPaymentMethod(): Promise<Models.PaymentMethod> {
        const apiPath = '/account/payment-methods';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'paymentMethod',
            models: {
                paymentMethod: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        providerMethodId: '',
                        clientSecret: '',
                        providerUserId: '',
                        userId: '',
                        default: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a specific payment method for the user.
     *
     * @param {string} params.paymentMethodId - Unique ID of payment method
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     */
    getPaymentMethod(params: {
        paymentMethodId: string;
    }): Promise<Models.PaymentMethod>;
    /**
     * Get a specific payment method for the user.
     *
     * @param {string} paymentMethodId - Unique ID of payment method
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getPaymentMethod(paymentMethodId: string): Promise<Models.PaymentMethod>;
    getPaymentMethod(
        paramsOrFirst: { paymentMethodId: string } | string,
    ): Promise<Models.PaymentMethod> {
        let params: { paymentMethodId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { paymentMethodId: string };
        } else {
            params = {
                paymentMethodId: paramsOrFirst as string,
            };
        }

        const paymentMethodId = params.paymentMethodId;

        if (typeof paymentMethodId === 'undefined' || paymentMethodId === '') {
            throw new AppwriteException(
                'Missing required parameter: "paymentMethodId"',
            );
        }
        const apiPath = '/account/payment-methods/{paymentMethodId}'.replace(
            '{paymentMethodId}',
            encodeURIComponent(String(paymentMethodId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'paymentMethod',
            models: {
                paymentMethod: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        providerMethodId: '',
                        clientSecret: '',
                        providerUserId: '',
                        userId: '',
                        default: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a new payment method for the current user account.
     *
     * @param {string} params.paymentMethodId - Unique ID of payment method
     * @param {number} params.expiryMonth - Payment expiry month
     * @param {number} params.expiryYear - Expiry year
     * @param {string} params.state - State of the payment method country
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     */
    updatePaymentMethod(params: {
        paymentMethodId: string;
        expiryMonth: number;
        expiryYear: number;
        state?: string;
    }): Promise<Models.PaymentMethod>;
    /**
     * Update a new payment method for the current user account.
     *
     * @param {string} paymentMethodId - Unique ID of payment method
     * @param {number} expiryMonth - Payment expiry month
     * @param {number} expiryYear - Expiry year
     * @param {string} state - State of the payment method country
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePaymentMethod(
        paymentMethodId: string,
        expiryMonth: number,
        expiryYear: number,
        state?: string,
    ): Promise<Models.PaymentMethod>;
    updatePaymentMethod(
        paramsOrFirst:
            | {
                  paymentMethodId: string;
                  expiryMonth: number;
                  expiryYear: number;
                  state?: string;
              }
            | string,
        ...rest: [number?, number?, string?]
    ): Promise<Models.PaymentMethod> {
        let params: {
            paymentMethodId: string;
            expiryMonth: number;
            expiryYear: number;
            state?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                paymentMethodId: string;
                expiryMonth: number;
                expiryYear: number;
                state?: string;
            };
        } else {
            params = {
                paymentMethodId: paramsOrFirst as string,
                expiryMonth: rest[0] as number,
                expiryYear: rest[1] as number,
                state: rest[2] as string,
            };
        }

        const paymentMethodId = params.paymentMethodId;
        const expiryMonth = params.expiryMonth;
        const expiryYear = params.expiryYear;
        const state = params.state;

        if (typeof paymentMethodId === 'undefined' || paymentMethodId === '') {
            throw new AppwriteException(
                'Missing required parameter: "paymentMethodId"',
            );
        }
        if (typeof expiryMonth === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "expiryMonth"',
            );
        }
        if (typeof expiryYear === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "expiryYear"',
            );
        }
        const apiPath = '/account/payment-methods/{paymentMethodId}'.replace(
            '{paymentMethodId}',
            encodeURIComponent(String(paymentMethodId)),
        );
        const apiPayload: Payload = {};
        if (typeof expiryMonth !== 'undefined') {
            apiPayload['expiryMonth'] = expiryMonth;
        }
        if (typeof expiryYear !== 'undefined') {
            apiPayload['expiryYear'] = expiryYear;
        }
        if (typeof state !== 'undefined') {
            apiPayload['state'] = state;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'paymentMethod',
            models: {
                paymentMethod: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        providerMethodId: '',
                        clientSecret: '',
                        providerUserId: '',
                        userId: '',
                        default: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a specific payment method from a user's account.
     *
     * @param {string} params.paymentMethodId - Unique ID of payment method
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deletePaymentMethod(params: { paymentMethodId: string }): Promise<{}>;
    /**
     * Delete a specific payment method from a user's account.
     *
     * @param {string} paymentMethodId - Unique ID of payment method
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deletePaymentMethod(paymentMethodId: string): Promise<{}>;
    deletePaymentMethod(
        paramsOrFirst: { paymentMethodId: string } | string,
    ): Promise<{}> {
        let params: { paymentMethodId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { paymentMethodId: string };
        } else {
            params = {
                paymentMethodId: paramsOrFirst as string,
            };
        }

        const paymentMethodId = params.paymentMethodId;

        if (typeof paymentMethodId === 'undefined' || paymentMethodId === '') {
            throw new AppwriteException(
                'Missing required parameter: "paymentMethodId"',
            );
        }
        const apiPath = '/account/payment-methods/{paymentMethodId}'.replace(
            '{paymentMethodId}',
            encodeURIComponent(String(paymentMethodId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Update payment method provider.
     *
     * @param {string} params.paymentMethodId - Unique ID of payment method
     * @param {string} params.providerMethodId - Payment method ID from the payment provider
     * @param {string} params.name - Name in the payment method
     * @param {string | null} params.state - State of the payment method country
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     */
    updatePaymentMethodProvider(params: {
        paymentMethodId: string;
        providerMethodId: string;
        name: string;
        state?: string | null;
    }): Promise<Models.PaymentMethod>;
    /**
     * Update payment method provider.
     *
     * @param {string} paymentMethodId - Unique ID of payment method
     * @param {string} providerMethodId - Payment method ID from the payment provider
     * @param {string} name - Name in the payment method
     * @param {string | null} state - State of the payment method country
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePaymentMethodProvider(
        paymentMethodId: string,
        providerMethodId: string,
        name: string,
        state?: string | null,
    ): Promise<Models.PaymentMethod>;
    updatePaymentMethodProvider(
        paramsOrFirst:
            | {
                  paymentMethodId: string;
                  providerMethodId: string;
                  name: string;
                  state?: string | null;
              }
            | string,
        ...rest: [string?, string?, (string | null)?]
    ): Promise<Models.PaymentMethod> {
        let params: {
            paymentMethodId: string;
            providerMethodId: string;
            name: string;
            state?: string | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                paymentMethodId: string;
                providerMethodId: string;
                name: string;
                state?: string | null;
            };
        } else {
            params = {
                paymentMethodId: paramsOrFirst as string,
                providerMethodId: rest[0] as string,
                name: rest[1] as string,
                state: rest[2] as string | null,
            };
        }

        const paymentMethodId = params.paymentMethodId;
        const providerMethodId = params.providerMethodId;
        const name = params.name;
        const state = params.state;

        if (typeof paymentMethodId === 'undefined' || paymentMethodId === '') {
            throw new AppwriteException(
                'Missing required parameter: "paymentMethodId"',
            );
        }
        if (typeof providerMethodId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "providerMethodId"',
            );
        }
        if (typeof name === 'undefined') {
            throw new AppwriteException('Missing required parameter: "name"');
        }
        const apiPath =
            '/account/payment-methods/{paymentMethodId}/provider'.replace(
                '{paymentMethodId}',
                encodeURIComponent(String(paymentMethodId)),
            );
        const apiPayload: Payload = {};
        if (typeof providerMethodId !== 'undefined') {
            apiPayload['providerMethodId'] = providerMethodId;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        if (typeof state !== 'undefined') {
            apiPayload['state'] = state;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'paymentMethod',
            models: {
                paymentMethod: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        providerMethodId: '',
                        clientSecret: '',
                        providerUserId: '',
                        userId: '',
                        default: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update payment method mandate options.
     *
     * @param {string} params.paymentMethodId - Unique ID of payment method
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     */
    updatePaymentMethodMandateOptions(params: {
        paymentMethodId: string;
    }): Promise<Models.PaymentMethod>;
    /**
     * Update payment method mandate options.
     *
     * @param {string} paymentMethodId - Unique ID of payment method
     * @throws {AppwriteException}
     * @returns {Promise<Models.PaymentMethod>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePaymentMethodMandateOptions(
        paymentMethodId: string,
    ): Promise<Models.PaymentMethod>;
    updatePaymentMethodMandateOptions(
        paramsOrFirst: { paymentMethodId: string } | string,
    ): Promise<Models.PaymentMethod> {
        let params: { paymentMethodId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { paymentMethodId: string };
        } else {
            params = {
                paymentMethodId: paramsOrFirst as string,
            };
        }

        const paymentMethodId = params.paymentMethodId;

        if (typeof paymentMethodId === 'undefined' || paymentMethodId === '') {
            throw new AppwriteException(
                'Missing required parameter: "paymentMethodId"',
            );
        }
        const apiPath =
            '/account/payment-methods/{paymentMethodId}/setup'.replace(
                '{paymentMethodId}',
                encodeURIComponent(String(paymentMethodId)),
            );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'paymentMethod',
            models: {
                paymentMethod: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        providerMethodId: '',
                        clientSecret: '',
                        providerUserId: '',
                        userId: '',
                        default: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the currently logged in user's phone number. After updating the phone number, the phone verification status will be reset. A confirmation SMS is not sent automatically, however you can use the [POST /account/verification/phone](https://appwrite.io/docs/references/cloud/client-web/account#createPhoneVerification) endpoint to send a confirmation SMS.
     *
     * @param {string} params.phone - Phone number. Format this number with a leading '+' and a country code, e.g., +16175551212.
     * @param {string} params.password - User password. Must be at least 8 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updatePhone<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        phone: string;
        password: string;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update the currently logged in user's phone number. After updating the phone number, the phone verification status will be reset. A confirmation SMS is not sent automatically, however you can use the [POST /account/verification/phone](https://appwrite.io/docs/references/cloud/client-web/account#createPhoneVerification) endpoint to send a confirmation SMS.
     *
     * @param {string} phone - Phone number. Format this number with a leading '+' and a country code, e.g., +16175551212.
     * @param {string} password - User password. Must be at least 8 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePhone<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(phone: string, password: string): Promise<Models.User<Preferences>>;
    updatePhone<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { phone: string; password: string } | string,
        ...rest: [string?]
    ): Promise<Models.User<Preferences>> {
        let params: { phone: string; password: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                phone: string;
                password: string;
            };
        } else {
            params = {
                phone: paramsOrFirst as string,
                password: rest[0] as string,
            };
        }

        const phone = params.phone;
        const password = params.password;

        if (typeof phone === 'undefined') {
            throw new AppwriteException('Missing required parameter: "phone"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/account/phone';
        const apiPayload: Payload = {};
        if (typeof phone !== 'undefined') {
            apiPayload['phone'] = phone;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the preferences as a key-value object for the currently logged in user.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Preferences>}
     */
    getPrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(): Promise<Preferences> {
        const apiPath = '/account/prefs';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        return this.client.call('get', uri, apiHeaders, apiPayload);
    }

    /**
     * Update currently logged in user account preferences. The object you pass is stored as is, and replaces any previous value. The maximum allowed prefs size is 64kB and throws error if exceeded.
     *
     * @param {Partial<Preferences>} params.prefs - Prefs key-value JSON object.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updatePrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(params: {
        prefs: Partial<Preferences>;
    }): Promise<Models.User<Preferences>>;
    /**
     * Update currently logged in user account preferences. The object you pass is stored as is, and replaces any previous value. The maximum allowed prefs size is 64kB and throws error if exceeded.
     *
     * @param {Partial<Preferences>} prefs - Prefs key-value JSON object.
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(prefs: Partial<Preferences>): Promise<Models.User<Preferences>>;
    updatePrefs<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(
        paramsOrFirst: { prefs: Partial<Preferences> } | Partial<Preferences>,
    ): Promise<Models.User<Preferences>> {
        let params: { prefs: Partial<Preferences> };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            'prefs' in paramsOrFirst
        ) {
            params = (paramsOrFirst || {}) as { prefs: Partial<Preferences> };
        } else {
            params = {
                prefs: paramsOrFirst as Partial<Preferences>,
            };
        }

        const prefs = params.prefs;

        if (typeof prefs === 'undefined') {
            throw new AppwriteException('Missing required parameter: "prefs"');
        }
        const apiPath = '/account/prefs';
        const apiPayload: Payload = {};
        if (typeof prefs !== 'undefined') {
            apiPayload['prefs'] = prefs;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Sends the user an email with a temporary secret key for password reset. When the user clicks the confirmation link he is redirected back to your app password reset URL with the secret key and email address values attached to the URL query string. Use the query string params to submit a request to the [PUT /account/recovery](https://appwrite.io/docs/references/cloud/client-web/account#updateRecovery) endpoint to complete the process. The verification link sent to the user's email address is valid for 1 hour.
     *
     * @param {string} params.email - User email.
     * @param {string} params.url - URL to redirect the user back to your app from the recovery email. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createRecovery(params: {
        email: string;
        url: string;
    }): Promise<Models.Token>;
    /**
     * Sends the user an email with a temporary secret key for password reset. When the user clicks the confirmation link he is redirected back to your app password reset URL with the secret key and email address values attached to the URL query string. Use the query string params to submit a request to the [PUT /account/recovery](https://appwrite.io/docs/references/cloud/client-web/account#updateRecovery) endpoint to complete the process. The verification link sent to the user's email address is valid for 1 hour.
     *
     * @param {string} email - User email.
     * @param {string} url - URL to redirect the user back to your app from the recovery email. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecovery(email: string, url: string): Promise<Models.Token>;
    createRecovery(
        paramsOrFirst: { email: string; url: string } | string,
        ...rest: [string?]
    ): Promise<Models.Token> {
        let params: { email: string; url: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { email: string; url: string };
        } else {
            params = {
                email: paramsOrFirst as string,
                url: rest[0] as string,
            };
        }

        const email = params.email;
        const url = params.url;

        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof url === 'undefined') {
            throw new AppwriteException('Missing required parameter: "url"');
        }
        const apiPath = '/account/recovery';
        const apiPayload: Payload = {};
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof url !== 'undefined') {
            apiPayload['url'] = url;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to complete the user account password reset. Both the **userId** and **secret** arguments will be passed as query parameters to the redirect URL you have provided when sending your request to the [POST /account/recovery](https://appwrite.io/docs/references/cloud/client-web/account#createRecovery) endpoint.
     *
     * Please note that in order to avoid a [Redirect Attack](https://github.com/OWASP/CheatSheetSeries/blob/master/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.md) the only valid redirect URLs are the ones from domains you have set when adding your platforms in the console interface.
     *
     * The new password must satisfy the project's enabled password policies, including custom deny lists. Dictionary entries match the entire password case-sensitively; personal-data terms match substrings case-insensitively. Personal-data validation runs after token verification against the recovered user's ID, name, email, and phone, closing the previous recovery gap for profile-based checks. A rejected password leaves the existing credentials unchanged and the recovery token usable.
     *
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.secret - Valid reset token.
     * @param {string} params.password - New user password. Must be between 8 and 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    updateRecovery(params: {
        userId: string;
        secret: string;
        password: string;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to complete the user account password reset. Both the **userId** and **secret** arguments will be passed as query parameters to the redirect URL you have provided when sending your request to the [POST /account/recovery](https://appwrite.io/docs/references/cloud/client-web/account#createRecovery) endpoint.
     *
     * Please note that in order to avoid a [Redirect Attack](https://github.com/OWASP/CheatSheetSeries/blob/master/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.md) the only valid redirect URLs are the ones from domains you have set when adding your platforms in the console interface.
     *
     * The new password must satisfy the project's enabled password policies, including custom deny lists. Dictionary entries match the entire password case-sensitively; personal-data terms match substrings case-insensitively. Personal-data validation runs after token verification against the recovered user's ID, name, email, and phone, closing the previous recovery gap for profile-based checks. A rejected password leaves the existing credentials unchanged and the recovery token usable.
     *
     *
     * @param {string} userId - User ID.
     * @param {string} secret - Valid reset token.
     * @param {string} password - New user password. Must be between 8 and 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecovery(
        userId: string,
        secret: string,
        password: string,
    ): Promise<Models.Token>;
    updateRecovery(
        paramsOrFirst:
            { userId: string; secret: string; password: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.Token> {
        let params: { userId: string; secret: string; password: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
                password: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
                password: rest[1] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;
        const password = params.password;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/account/recovery';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to send a 6-digit password recovery code to the user's email address. Unlike [createRecovery](https://appwrite.io/docs/references/cloud/client-web/account#createRecovery), this method requires no redirect URL, which makes it suitable for mobile and desktop apps that cannot host a recovery page. Learn more about how to [complete the recovery process](https://appwrite.io/docs/references/cloud/client-web/account#updateRecoveryOTP). The code sent to the user's email address is valid for 15 minutes.
     *
     * Enable the **phrase** parameter to include a randomly generated security phrase in both the email and the response. Showing that phrase in your app lets the user confirm the email genuinely came from your request, which helps protect against phishing.
     *
     *
     * @param {string} params.email - User email.
     * @param {boolean} params.phrase - Toggle for security phrase. If enabled, email will be sent with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createRecoveryOTP(params: {
        email: string;
        phrase?: boolean;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to send a 6-digit password recovery code to the user's email address. Unlike [createRecovery](https://appwrite.io/docs/references/cloud/client-web/account#createRecovery), this method requires no redirect URL, which makes it suitable for mobile and desktop apps that cannot host a recovery page. Learn more about how to [complete the recovery process](https://appwrite.io/docs/references/cloud/client-web/account#updateRecoveryOTP). The code sent to the user's email address is valid for 15 minutes.
     *
     * Enable the **phrase** parameter to include a randomly generated security phrase in both the email and the response. Showing that phrase in your app lets the user confirm the email genuinely came from your request, which helps protect against phishing.
     *
     *
     * @param {string} email - User email.
     * @param {boolean} phrase - Toggle for security phrase. If enabled, email will be sent with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createRecoveryOTP(email: string, phrase?: boolean): Promise<Models.Token>;
    createRecoveryOTP(
        paramsOrFirst: { email: string; phrase?: boolean } | string,
        ...rest: [boolean?]
    ): Promise<Models.Token> {
        let params: { email: string; phrase?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                email: string;
                phrase?: boolean;
            };
        } else {
            params = {
                email: paramsOrFirst as string,
                phrase: rest[0] as boolean,
            };
        }

        const email = params.email;
        const phrase = params.phrase;

        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        const apiPath = '/account/recovery/otp';
        const apiPayload: Payload = {};
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof phrase !== 'undefined') {
            apiPayload['phrase'] = phrase;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to complete the user password recovery process using the 6-digit code that was emailed by [createRecoveryOTP](https://appwrite.io/docs/references/cloud/client-web/account#createRecoveryOTP). Pass the **userId** of the user along with the **secret** code from the email and the new **password** to set. If confirmed, this route will return a 200 status code, the code is consumed and the user's password is updated.
     *
     * The new password must satisfy the project's enabled password policies, including custom deny lists. Dictionary entries match the entire password case-sensitively; personal-data terms match substrings case-insensitively. Personal-data validation runs after token verification against the recovered user's ID, name, email, and phone, closing the previous recovery gap for profile-based checks. A rejected password leaves the existing credentials unchanged and the recovery token usable.
     *
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.secret - Valid recovery OTP code.
     * @param {string} params.password - New user password. Must be between 8 and 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    updateRecoveryOTP(params: {
        userId: string;
        secret: string;
        password: string;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to complete the user password recovery process using the 6-digit code that was emailed by [createRecoveryOTP](https://appwrite.io/docs/references/cloud/client-web/account#createRecoveryOTP). Pass the **userId** of the user along with the **secret** code from the email and the new **password** to set. If confirmed, this route will return a 200 status code, the code is consumed and the user's password is updated.
     *
     * The new password must satisfy the project's enabled password policies, including custom deny lists. Dictionary entries match the entire password case-sensitively; personal-data terms match substrings case-insensitively. Personal-data validation runs after token verification against the recovered user's ID, name, email, and phone, closing the previous recovery gap for profile-based checks. A rejected password leaves the existing credentials unchanged and the recovery token usable.
     *
     *
     * @param {string} userId - User ID.
     * @param {string} secret - Valid recovery OTP code.
     * @param {string} password - New user password. Must be between 8 and 256 chars.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateRecoveryOTP(
        userId: string,
        secret: string,
        password: string,
    ): Promise<Models.Token>;
    updateRecoveryOTP(
        paramsOrFirst:
            { userId: string; secret: string; password: string } | string,
        ...rest: [string?, string?]
    ): Promise<Models.Token> {
        let params: { userId: string; secret: string; password: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
                password: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
                password: rest[1] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;
        const password = params.password;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/account/recovery/otp';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get the list of active sessions across different devices for the currently logged in user.
     *
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SessionList>}
     */
    listSessions(params?: { total?: boolean }): Promise<Models.SessionList>;
    /**
     * Get the list of active sessions across different devices for the currently logged in user.
     *
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @throws {AppwriteException}
     * @returns {Promise<Models.SessionList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    listSessions(total?: boolean): Promise<Models.SessionList>;
    listSessions(
        paramsOrFirst?: { total?: boolean } | boolean,
    ): Promise<Models.SessionList> {
        let params: { total?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { total?: boolean };
        } else {
            params = {
                total: paramsOrFirst as boolean,
            };
        }

        const total = params.total;

        const apiPath = '/account/sessions';
        const apiPayload: Payload = {};
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'sessionList',
            models: {
                sessionList: {
                    defaults: {
                        total: 0,
                        sessions: [],
                    },
                    properties: {
                        sessions: {
                            items: 'session',
                        },
                    },
                },
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete all sessions from the user account and remove any sessions cookies from the end client. Pass `current` as false to keep the session making the request and sign out of every other session.
     *
     * @param {boolean} params.current - Delete the current session too. Use false to sign out of every other session while staying signed in on this one.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteSessions(params?: { current?: boolean }): Promise<{}>;
    /**
     * Delete all sessions from the user account and remove any sessions cookies from the end client. Pass `current` as false to keep the session making the request and sign out of every other session.
     *
     * @param {boolean} current - Delete the current session too. Use false to sign out of every other session while staying signed in on this one.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteSessions(current?: boolean): Promise<{}>;
    deleteSessions(
        paramsOrFirst?: { current?: boolean } | boolean,
    ): Promise<{}> {
        let params: { current?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { current?: boolean };
        } else {
            params = {
                current: paramsOrFirst as boolean,
            };
        }

        const current = params.current;

        const apiPath = '/account/sessions';
        const apiPayload: Payload = {};
        if (typeof current !== 'undefined') {
            apiPayload['current'] = current;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Use this endpoint to allow a new user to register an anonymous account in your project. This route will also create a new session for the user. To allow the new user to convert an anonymous account to a normal account, you need to update its [email and password](https://appwrite.io/docs/references/cloud/client-web/account#updateEmail) or create an [OAuth2 session](https://appwrite.io/docs/references/cloud/client-web/account#CreateOAuth2Session).
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    createAnonymousSession(): Promise<Models.Session> {
        const apiPath = '/account/sessions/anonymous';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Allow the user to login into their account by providing a valid email and password combination. This route will create a new session for the user. Use the optional `duration` parameter to create a shorter session, for example when the user doesn't choose "remember me". It must be at least 60 seconds and cannot exceed the project maximum session length.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     * @param {string} params.email - User email.
     * @param {string} params.password - User password. Must be at least 8 chars.
     * @param {number | null} params.duration - Session length in seconds. Minimum is 60 seconds, and it cannot exceed the project maximum session length. Defaults to the project maximum session length.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    createEmailPasswordSession(params: {
        email: string;
        password: string;
        duration?: number | null;
    }): Promise<Models.Session>;
    /**
     * Allow the user to login into their account by providing a valid email and password combination. This route will create a new session for the user. Use the optional `duration` parameter to create a shorter session, for example when the user doesn't choose "remember me". It must be at least 60 seconds and cannot exceed the project maximum session length.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     * @param {string} email - User email.
     * @param {string} password - User password. Must be at least 8 chars.
     * @param {number | null} duration - Session length in seconds. Minimum is 60 seconds, and it cannot exceed the project maximum session length. Defaults to the project maximum session length.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createEmailPasswordSession(
        email: string,
        password: string,
        duration?: number | null,
    ): Promise<Models.Session>;
    createEmailPasswordSession(
        paramsOrFirst:
            | { email: string; password: string; duration?: number | null }
            | string,
        ...rest: [string?, (number | null)?]
    ): Promise<Models.Session> {
        let params: {
            email: string;
            password: string;
            duration?: number | null;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                email: string;
                password: string;
                duration?: number | null;
            };
        } else {
            params = {
                email: paramsOrFirst as string,
                password: rest[0] as string,
                duration: rest[1] as number | null,
            };
        }

        const email = params.email;
        const password = params.password;
        const duration = params.duration;

        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        if (typeof password === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "password"',
            );
        }
        const apiPath = '/account/sessions/email';
        const apiPayload: Payload = {};
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof password !== 'undefined') {
            apiPayload['password'] = password;
        }
        if (typeof duration !== 'undefined') {
            apiPayload['duration'] = duration;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Allow the user to login to their account using an OpenID Connect ID token obtained natively from the OAuth2 provider, for example via Google Credential Manager on Android or Sign in with Apple on iOS. No browser or redirect is involved: the ID token is verified against the provider's published signing keys and a session is created in a single request.
     *
     * Native sign-in is switched on per provider with its nativeEnabled setting. It is independent of the browser-based flow's enabled setting, which has no effect on this endpoint. The token's audience must match the provider's configured client ID or one of its native client IDs; tokens issued for any other client ID are rejected. For Sign in with Apple, register your app's bundle ID as a native client ID. For Google, the web client ID used by Credential Manager is usually the configured client ID; add your Android and iOS client IDs as native client IDs if your app requests tokens for them.
     *
     * Pass the raw nonce used when requesting the ID token so it can be validated against the token's nonce claim. When signing in with Apple, the nonce is required: hash it with SHA-256 before passing it to the Apple SDK, and send the raw value here - Apple tokens requested without a nonce are rejected. For Google the nonce is optional: it is validated whenever the token carries one, and ignored when the provider issued the token without one. Apple only returns the user's name on the first authorization, and never inside the ID token - capture it on the client and pass it via the name parameter.
     *
     * If there is already an active session, the new session will be attached to the logged-in account. If there are no active sessions, the server will attempt to look for a user with the same email address as the verified email received from the provider and attach the new session to the existing user. If no matching user is found - the server will create a new user.
     *
     * This flow does not return provider refresh tokens. You may pass an access token the provider handed your client, along with its lifetime, to store it on the session - but Appwrite cannot renew it once it expires. If your app needs long-lived access to provider APIs, use the browser-based OAuth2 flow instead.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {IdTokenProvider} params.provider - OAuth2 provider that issued the ID token. Currently, supported providers are: apple, google.
     * @param {string} params.idToken - OpenID Connect ID token (JWT) obtained natively from the provider, for example via Google Credential Manager or Sign in with Apple.
     * @param {string} params.nonce - Raw nonce used when requesting the ID token. Required for Apple, and whenever the token carries a nonce claim, which must match it. Ignored when the provider issued the token without a nonce.
     * @param {string} params.accessToken - Provider access token to store alongside the session for calling provider APIs. Never used for authentication.
     * @param {number} params.accessTokenExpiry - Seconds until the provider access token expires, as reported by the provider. Stored so clients can tell when the stored token goes stale.
     * @param {string} params.name - User name. Only used when creating a new user and the ID token has no name claim, such as on the first Sign in with Apple authorization.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    createIdTokenSession(params: {
        provider: IdTokenProvider;
        idToken: string;
        nonce?: string;
        accessToken?: string;
        accessTokenExpiry?: number;
        name?: string;
    }): Promise<Models.Session>;
    /**
     * Allow the user to login to their account using an OpenID Connect ID token obtained natively from the OAuth2 provider, for example via Google Credential Manager on Android or Sign in with Apple on iOS. No browser or redirect is involved: the ID token is verified against the provider's published signing keys and a session is created in a single request.
     *
     * Native sign-in is switched on per provider with its nativeEnabled setting. It is independent of the browser-based flow's enabled setting, which has no effect on this endpoint. The token's audience must match the provider's configured client ID or one of its native client IDs; tokens issued for any other client ID are rejected. For Sign in with Apple, register your app's bundle ID as a native client ID. For Google, the web client ID used by Credential Manager is usually the configured client ID; add your Android and iOS client IDs as native client IDs if your app requests tokens for them.
     *
     * Pass the raw nonce used when requesting the ID token so it can be validated against the token's nonce claim. When signing in with Apple, the nonce is required: hash it with SHA-256 before passing it to the Apple SDK, and send the raw value here - Apple tokens requested without a nonce are rejected. For Google the nonce is optional: it is validated whenever the token carries one, and ignored when the provider issued the token without one. Apple only returns the user's name on the first authorization, and never inside the ID token - capture it on the client and pass it via the name parameter.
     *
     * If there is already an active session, the new session will be attached to the logged-in account. If there are no active sessions, the server will attempt to look for a user with the same email address as the verified email received from the provider and attach the new session to the existing user. If no matching user is found - the server will create a new user.
     *
     * This flow does not return provider refresh tokens. You may pass an access token the provider handed your client, along with its lifetime, to store it on the session - but Appwrite cannot renew it once it expires. If your app needs long-lived access to provider APIs, use the browser-based OAuth2 flow instead.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {IdTokenProvider} provider - OAuth2 provider that issued the ID token. Currently, supported providers are: apple, google.
     * @param {string} idToken - OpenID Connect ID token (JWT) obtained natively from the provider, for example via Google Credential Manager or Sign in with Apple.
     * @param {string} nonce - Raw nonce used when requesting the ID token. Required for Apple, and whenever the token carries a nonce claim, which must match it. Ignored when the provider issued the token without a nonce.
     * @param {string} accessToken - Provider access token to store alongside the session for calling provider APIs. Never used for authentication.
     * @param {number} accessTokenExpiry - Seconds until the provider access token expires, as reported by the provider. Stored so clients can tell when the stored token goes stale.
     * @param {string} name - User name. Only used when creating a new user and the ID token has no name claim, such as on the first Sign in with Apple authorization.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createIdTokenSession(
        provider: IdTokenProvider,
        idToken: string,
        nonce?: string,
        accessToken?: string,
        accessTokenExpiry?: number,
        name?: string,
    ): Promise<Models.Session>;
    createIdTokenSession(
        paramsOrFirst:
            | {
                  provider: IdTokenProvider;
                  idToken: string;
                  nonce?: string;
                  accessToken?: string;
                  accessTokenExpiry?: number;
                  name?: string;
              }
            | IdTokenProvider,
        ...rest: [string?, string?, string?, number?, string?]
    ): Promise<Models.Session> {
        let params: {
            provider: IdTokenProvider;
            idToken: string;
            nonce?: string;
            accessToken?: string;
            accessTokenExpiry?: number;
            name?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('provider' in paramsOrFirst ||
                'idToken' in paramsOrFirst ||
                'nonce' in paramsOrFirst ||
                'accessToken' in paramsOrFirst ||
                'accessTokenExpiry' in paramsOrFirst ||
                'name' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                provider: IdTokenProvider;
                idToken: string;
                nonce?: string;
                accessToken?: string;
                accessTokenExpiry?: number;
                name?: string;
            };
        } else {
            params = {
                provider: paramsOrFirst as IdTokenProvider,
                idToken: rest[0] as string,
                nonce: rest[1] as string,
                accessToken: rest[2] as string,
                accessTokenExpiry: rest[3] as number,
                name: rest[4] as string,
            };
        }

        const provider = params.provider;
        const idToken = params.idToken;
        const nonce = params.nonce;
        const accessToken = params.accessToken;
        const accessTokenExpiry = params.accessTokenExpiry;
        const name = params.name;

        if (typeof provider === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "provider"',
            );
        }
        if (typeof idToken === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "idToken"',
            );
        }
        const apiPath = '/account/sessions/id-token';
        const apiPayload: Payload = {};
        if (typeof provider !== 'undefined') {
            apiPayload['provider'] = provider;
        }
        if (typeof idToken !== 'undefined') {
            apiPayload['idToken'] = idToken;
        }
        if (typeof nonce !== 'undefined') {
            apiPayload['nonce'] = nonce;
        }
        if (typeof accessToken !== 'undefined') {
            apiPayload['accessToken'] = accessToken;
        }
        if (typeof accessTokenExpiry !== 'undefined') {
            apiPayload['accessTokenExpiry'] = accessTokenExpiry;
        }
        if (typeof name !== 'undefined') {
            apiPayload['name'] = name;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to create a session from token. Provide the **userId** and **secret** parameters from the successful response of authentication flows initiated by token creation. For example, magic URL and phone login.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated This API has been deprecated since 1.6.0. Please use `Account.createSession` instead.
     */
    updateMagicURLSession(params: {
        userId: string;
        secret: string;
    }): Promise<Models.Session>;
    /**
     * Use this endpoint to create a session from token. Provide the **userId** and **secret** parameters from the successful response of authentication flows initiated by token creation. For example, magic URL and phone login.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateMagicURLSession(
        userId: string,
        secret: string,
    ): Promise<Models.Session>;
    updateMagicURLSession(
        paramsOrFirst: { userId: string; secret: string } | string,
        ...rest: [string?]
    ): Promise<Models.Session> {
        let params: { userId: string; secret: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        const apiPath = '/account/sessions/magic-url';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Allow the user to login to their account using the OAuth2 provider of their choice. Each OAuth2 provider should be enabled from the Appwrite console first. Use the success and failure arguments to provide a redirect URL's back to your app when login is completed.
     *
     * If there is already an active session, the new session will be attached to the logged-in account. If there are no active sessions, the server will attempt to look for a user with the same email address as the email received from the OAuth2 provider and attach the new session to the existing user. If no matching user is found - the server will create a new user.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {OAuthProvider} params.provider - OAuth2 Provider. Currently, supported providers are: amazon, apple, appwrite, auth0, authentik, autodesk, bitbucket, bitly, box, cloudflare, dailymotion, discord, disqus, dropbox, etsy, facebook, figma, fusionauth, github, gitlab, google, huggingface, kakao, keycloak, kick, linkedin, microsoft, notion, oidc, okta, paypal, paypalSandbox, podio, resend, salesforce, slack, spotify, stripe, tiktok, tradeshift, tradeshiftBox, twitch, webflow, wordpress, x, yahoo, yammer, yandex, zoho, zoom.
     * @param {string} params.success - URL to redirect back to your app after a successful login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string} params.failure - URL to redirect back to your app after a failed login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string[]} params.scopes - A list of custom OAuth2 scopes. Check each provider internal docs for a list of supported scopes. Maximum of 100 scopes are allowed, each 4096 characters long.
     * @param {string} params.state - An opaque value your app generates and keeps, for example in a cookie. It is returned unchanged as the `state` query parameter on the success and failure URLs, so your app can check that the sign-in it receives is one it started. Printable ASCII only (RFC 6749 Appendix A.5). Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {void | string}
     */
    createOAuth2Session(params: {
        provider: OAuthProvider;
        success?: string;
        failure?: string;
        scopes?: string[];
        state?: string;
    }): void | string;
    /**
     * Allow the user to login to their account using the OAuth2 provider of their choice. Each OAuth2 provider should be enabled from the Appwrite console first. Use the success and failure arguments to provide a redirect URL's back to your app when login is completed.
     *
     * If there is already an active session, the new session will be attached to the logged-in account. If there are no active sessions, the server will attempt to look for a user with the same email address as the email received from the OAuth2 provider and attach the new session to the existing user. If no matching user is found - the server will create a new user.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {OAuthProvider} provider - OAuth2 Provider. Currently, supported providers are: amazon, apple, appwrite, auth0, authentik, autodesk, bitbucket, bitly, box, cloudflare, dailymotion, discord, disqus, dropbox, etsy, facebook, figma, fusionauth, github, gitlab, google, huggingface, kakao, keycloak, kick, linkedin, microsoft, notion, oidc, okta, paypal, paypalSandbox, podio, resend, salesforce, slack, spotify, stripe, tiktok, tradeshift, tradeshiftBox, twitch, webflow, wordpress, x, yahoo, yammer, yandex, zoho, zoom.
     * @param {string} success - URL to redirect back to your app after a successful login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string} failure - URL to redirect back to your app after a failed login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string[]} scopes - A list of custom OAuth2 scopes. Check each provider internal docs for a list of supported scopes. Maximum of 100 scopes are allowed, each 4096 characters long.
     * @param {string} state - An opaque value your app generates and keeps, for example in a cookie. It is returned unchanged as the `state` query parameter on the success and failure URLs, so your app can check that the sign-in it receives is one it started. Printable ASCII only (RFC 6749 Appendix A.5). Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {void | string}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createOAuth2Session(
        provider: OAuthProvider,
        success?: string,
        failure?: string,
        scopes?: string[],
        state?: string,
    ): void | string;
    createOAuth2Session(
        paramsOrFirst:
            | {
                  provider: OAuthProvider;
                  success?: string;
                  failure?: string;
                  scopes?: string[];
                  state?: string;
              }
            | OAuthProvider,
        ...rest: [string?, string?, string[]?, string?]
    ): void | string {
        let params: {
            provider: OAuthProvider;
            success?: string;
            failure?: string;
            scopes?: string[];
            state?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('provider' in paramsOrFirst ||
                'success' in paramsOrFirst ||
                'failure' in paramsOrFirst ||
                'scopes' in paramsOrFirst ||
                'state' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                provider: OAuthProvider;
                success?: string;
                failure?: string;
                scopes?: string[];
                state?: string;
            };
        } else {
            params = {
                provider: paramsOrFirst as OAuthProvider,
                success: rest[0] as string,
                failure: rest[1] as string,
                scopes: rest[2] as string[],
                state: rest[3] as string,
            };
        }

        const provider = params.provider;
        const success = params.success;
        const failure = params.failure;
        const scopes = params.scopes;
        const state = params.state;

        if (typeof provider === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "provider"',
            );
        }
        const apiPath = '/account/sessions/oauth2/{provider}'.replace(
            '{provider}',
            encodeURIComponent(String(provider)),
        );
        const apiPayload: Payload = {};
        if (typeof success !== 'undefined') {
            apiPayload['success'] = success;
        }
        if (typeof failure !== 'undefined') {
            apiPayload['failure'] = failure;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof state !== 'undefined') {
            apiPayload['state'] = state;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        apiPayload['project'] = this.client.config.project;

        for (const [key, value] of Object.entries(Client.flatten(apiPayload))) {
            uri.searchParams.append(key, value);
        }

        if (typeof window !== 'undefined' && window?.location) {
            window.location.href = uri.toString();
            return;
        } else {
            return uri.toString();
        }
    }

    /**
     * Use this endpoint to create a session from token. Provide the **userId** and **secret** parameters from the successful response of authentication flows initiated by token creation. For example, magic URL and phone login.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated This API has been deprecated since 1.6.0. Please use `Account.createSession` instead.
     */
    updatePhoneSession(params: {
        userId: string;
        secret: string;
    }): Promise<Models.Session>;
    /**
     * Use this endpoint to create a session from token. Provide the **userId** and **secret** parameters from the successful response of authentication flows initiated by token creation. For example, magic URL and phone login.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePhoneSession(userId: string, secret: string): Promise<Models.Session>;
    updatePhoneSession(
        paramsOrFirst: { userId: string; secret: string } | string,
        ...rest: [string?]
    ): Promise<Models.Session> {
        let params: { userId: string; secret: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        const apiPath = '/account/sessions/phone';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to create a session from token. Provide the **userId** and **secret** parameters from the successful response of authentication flows initiated by token creation. For example, magic URL and phone login.
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.secret - Secret of a token generated by login methods. For example, the `createMagicURLToken` or `createPhoneToken` methods.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    createSession(params: {
        userId: string;
        secret: string;
    }): Promise<Models.Session>;
    /**
     * Use this endpoint to create a session from token. Provide the **userId** and **secret** parameters from the successful response of authentication flows initiated by token creation. For example, magic URL and phone login.
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} secret - Secret of a token generated by login methods. For example, the `createMagicURLToken` or `createPhoneToken` methods.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createSession(userId: string, secret: string): Promise<Models.Session>;
    createSession(
        paramsOrFirst: { userId: string; secret: string } | string,
        ...rest: [string?]
    ): Promise<Models.Session> {
        let params: { userId: string; secret: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        const apiPath = '/account/sessions/token';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to get a logged in user's session using a Session ID. Inputting 'current' will return the current session being used.
     *
     * @param {string} params.sessionId - Session ID. Use the string 'current' to get the current device session.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    getSession(params: { sessionId: string }): Promise<Models.Session>;
    /**
     * Use this endpoint to get a logged in user's session using a Session ID. Inputting 'current' will return the current session being used.
     *
     * @param {string} sessionId - Session ID. Use the string 'current' to get the current device session.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    getSession(sessionId: string): Promise<Models.Session>;
    getSession(
        paramsOrFirst: { sessionId: string } | string,
    ): Promise<Models.Session> {
        let params: { sessionId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { sessionId: string };
        } else {
            params = {
                sessionId: paramsOrFirst as string,
            };
        }

        const sessionId = params.sessionId;

        if (typeof sessionId === 'undefined' || sessionId === '') {
            throw new AppwriteException(
                'Missing required parameter: "sessionId"',
            );
        }
        const apiPath = '/account/sessions/{sessionId}'.replace(
            '{sessionId}',
            encodeURIComponent(String(sessionId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to extend a session's length. Extending a session is useful when session expiry is short. If the session was created using an OAuth provider, this endpoint refreshes the access token from the provider.
     *
     * @param {string} params.sessionId - Session ID. Use the string 'current' to update the current device session.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     */
    updateSession(params: { sessionId: string }): Promise<Models.Session>;
    /**
     * Use this endpoint to extend a session's length. Extending a session is useful when session expiry is short. If the session was created using an OAuth provider, this endpoint refreshes the access token from the provider.
     *
     * @param {string} sessionId - Session ID. Use the string 'current' to update the current device session.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Session>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateSession(sessionId: string): Promise<Models.Session>;
    updateSession(
        paramsOrFirst: { sessionId: string } | string,
    ): Promise<Models.Session> {
        let params: { sessionId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { sessionId: string };
        } else {
            params = {
                sessionId: paramsOrFirst as string,
            };
        }

        const sessionId = params.sessionId;

        if (typeof sessionId === 'undefined' || sessionId === '') {
            throw new AppwriteException(
                'Missing required parameter: "sessionId"',
            );
        }
        const apiPath = '/account/sessions/{sessionId}'.replace(
            '{sessionId}',
            encodeURIComponent(String(sessionId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'session',
            models: {
                session: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        userId: '',
                        expire: '',
                        provider: '',
                        providerUid: '',
                        providerAccessToken: '',
                        providerAccessTokenExpiry: '',
                        providerRefreshToken: '',
                        ip: '',
                        osCode: '',
                        osName: '',
                        osVersion: '',
                        clientType: '',
                        clientCode: '',
                        clientName: '',
                        clientVersion: '',
                        clientEngine: '',
                        clientEngineVersion: '',
                        deviceName: '',
                        deviceBrand: '',
                        deviceModel: '',
                        countryCode: '',
                        countryName: '',
                        current: false,
                        factors: [],
                        secret: '',
                        mfaUpdatedAt: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Logout the user. Use 'current' as the session ID to logout on this device, use a session ID to logout on another device. If you're looking to logout the user on all devices, use [Delete Sessions](https://appwrite.io/docs/references/cloud/client-web/account#deleteSessions) instead.
     *
     * @param {string} params.sessionId - Session ID. Use the string 'current' to delete the current device session.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deleteSession(params: { sessionId: string }): Promise<{}>;
    /**
     * Logout the user. Use 'current' as the session ID to logout on this device, use a session ID to logout on another device. If you're looking to logout the user on all devices, use [Delete Sessions](https://appwrite.io/docs/references/cloud/client-web/account#deleteSessions) instead.
     *
     * @param {string} sessionId - Session ID. Use the string 'current' to delete the current device session.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deleteSession(sessionId: string): Promise<{}>;
    deleteSession(paramsOrFirst: { sessionId: string } | string): Promise<{}> {
        let params: { sessionId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { sessionId: string };
        } else {
            params = {
                sessionId: paramsOrFirst as string,
            };
        }

        const sessionId = params.sessionId;

        if (typeof sessionId === 'undefined' || sessionId === '') {
            throw new AppwriteException(
                'Missing required parameter: "sessionId"',
            );
        }
        const apiPath = '/account/sessions/{sessionId}'.replace(
            '{sessionId}',
            encodeURIComponent(String(sessionId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Block the currently logged in user account. Behind the scene, the user record is not deleted but permanently blocked from any access. To completely delete a user, use the Users API instead.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.User<Preferences>>}
     */
    updateStatus<
        Preferences extends Models.Preferences = Models.DefaultPreferences,
    >(): Promise<Models.User<Preferences>> {
        const apiPath = '/account/status';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'user',
            models: {
                user: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        registration: '',
                        status: true,
                        labels: [],
                        passwordUpdate: '',
                        email: '',
                        phone: '',
                        emailVerification: false,
                        phoneVerification: false,
                        mfa: false,
                        targets: [],
                        accessedAt: '',
                    },
                    properties: {
                        targets: {
                            items: 'target',
                        },
                    },
                },
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to register a device for push notifications. Provide a target ID (custom or generated using ID.unique()), a device identifier (usually a device token), and optionally specify which provider should send notifications to this target. The target is automatically linked to the current session and includes device information like brand and model. A session holds one push target per provider, so if one already exists this endpoint updates and returns that target instead of creating a second one, and a device that rotates its token is never notified twice.
     *
     * @param {string} params.targetId - Target ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} params.identifier - The target identifier (token, email, phone etc.)
     * @param {string} params.providerId - Provider ID. Message will be sent to this target from the specified provider ID. If no provider ID is set the first setup provider will be used.
     * @param {string} params.bundleId - App bundle ID of the device registering an APNs token. Without `providerId`, routes the target to the enabled APNs provider for this bundle ID and `environment`.
     * @param {string} params.environment - APNs environment the token belongs to, `sandbox` or `production`. Used with `bundleId`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     */
    createPushTarget(params: {
        targetId: string;
        identifier: string;
        providerId?: string;
        bundleId?: string;
        environment?: string;
    }): Promise<Models.Target>;
    /**
     * Use this endpoint to register a device for push notifications. Provide a target ID (custom or generated using ID.unique()), a device identifier (usually a device token), and optionally specify which provider should send notifications to this target. The target is automatically linked to the current session and includes device information like brand and model. A session holds one push target per provider, so if one already exists this endpoint updates and returns that target instead of creating a second one, and a device that rotates its token is never notified twice.
     *
     * @param {string} targetId - Target ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars.
     * @param {string} identifier - The target identifier (token, email, phone etc.)
     * @param {string} providerId - Provider ID. Message will be sent to this target from the specified provider ID. If no provider ID is set the first setup provider will be used.
     * @param {string} bundleId - App bundle ID of the device registering an APNs token. Without `providerId`, routes the target to the enabled APNs provider for this bundle ID and `environment`.
     * @param {string} environment - APNs environment the token belongs to, `sandbox` or `production`. Used with `bundleId`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPushTarget(
        targetId: string,
        identifier: string,
        providerId?: string,
        bundleId?: string,
        environment?: string,
    ): Promise<Models.Target>;
    createPushTarget(
        paramsOrFirst:
            | {
                  targetId: string;
                  identifier: string;
                  providerId?: string;
                  bundleId?: string;
                  environment?: string;
              }
            | string,
        ...rest: [string?, string?, string?, string?]
    ): Promise<Models.Target> {
        let params: {
            targetId: string;
            identifier: string;
            providerId?: string;
            bundleId?: string;
            environment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                targetId: string;
                identifier: string;
                providerId?: string;
                bundleId?: string;
                environment?: string;
            };
        } else {
            params = {
                targetId: paramsOrFirst as string,
                identifier: rest[0] as string,
                providerId: rest[1] as string,
                bundleId: rest[2] as string,
                environment: rest[3] as string,
            };
        }

        const targetId = params.targetId;
        const identifier = params.identifier;
        const providerId = params.providerId;
        const bundleId = params.bundleId;
        const environment = params.environment;

        if (typeof targetId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "targetId"',
            );
        }
        if (typeof identifier === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "identifier"',
            );
        }
        const apiPath = '/account/targets/push';
        const apiPayload: Payload = {};
        if (typeof targetId !== 'undefined') {
            apiPayload['targetId'] = targetId;
        }
        if (typeof identifier !== 'undefined') {
            apiPayload['identifier'] = identifier;
        }
        if (typeof providerId !== 'undefined') {
            apiPayload['providerId'] = providerId;
        }
        if (typeof bundleId !== 'undefined') {
            apiPayload['bundleId'] = bundleId;
        }
        if (typeof environment !== 'undefined') {
            apiPayload['environment'] = environment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'target',
            models: {
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update the currently logged in user's push notification target. You can modify the target's identifier (device token) and provider ID (token, email, phone etc.). The target must exist and belong to the current user. If you change the provider ID, notifications will be sent through the new messaging provider instead.
     *
     * @param {string} params.targetId - Target ID.
     * @param {string} params.identifier - The target identifier (token, email, phone etc.)
     * @param {string} params.bundleId - App bundle ID of the device registering an APNs token. Routes an APNs target to the enabled APNs provider for this bundle ID and `environment`.
     * @param {string} params.environment - APNs environment the token belongs to, `sandbox` or `production`. Used with `bundleId`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     */
    updatePushTarget(params: {
        targetId: string;
        identifier: string;
        bundleId?: string;
        environment?: string;
    }): Promise<Models.Target>;
    /**
     * Update the currently logged in user's push notification target. You can modify the target's identifier (device token) and provider ID (token, email, phone etc.). The target must exist and belong to the current user. If you change the provider ID, notifications will be sent through the new messaging provider instead.
     *
     * @param {string} targetId - Target ID.
     * @param {string} identifier - The target identifier (token, email, phone etc.)
     * @param {string} bundleId - App bundle ID of the device registering an APNs token. Routes an APNs target to the enabled APNs provider for this bundle ID and `environment`.
     * @param {string} environment - APNs environment the token belongs to, `sandbox` or `production`. Used with `bundleId`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Target>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePushTarget(
        targetId: string,
        identifier: string,
        bundleId?: string,
        environment?: string,
    ): Promise<Models.Target>;
    updatePushTarget(
        paramsOrFirst:
            | {
                  targetId: string;
                  identifier: string;
                  bundleId?: string;
                  environment?: string;
              }
            | string,
        ...rest: [string?, string?, string?]
    ): Promise<Models.Target> {
        let params: {
            targetId: string;
            identifier: string;
            bundleId?: string;
            environment?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                targetId: string;
                identifier: string;
                bundleId?: string;
                environment?: string;
            };
        } else {
            params = {
                targetId: paramsOrFirst as string,
                identifier: rest[0] as string,
                bundleId: rest[1] as string,
                environment: rest[2] as string,
            };
        }

        const targetId = params.targetId;
        const identifier = params.identifier;
        const bundleId = params.bundleId;
        const environment = params.environment;

        if (typeof targetId === 'undefined' || targetId === '') {
            throw new AppwriteException(
                'Missing required parameter: "targetId"',
            );
        }
        if (typeof identifier === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "identifier"',
            );
        }
        const apiPath = '/account/targets/{targetId}/push'.replace(
            '{targetId}',
            encodeURIComponent(String(targetId)),
        );
        const apiPayload: Payload = {};
        if (typeof identifier !== 'undefined') {
            apiPayload['identifier'] = identifier;
        }
        if (typeof bundleId !== 'undefined') {
            apiPayload['bundleId'] = bundleId;
        }
        if (typeof environment !== 'undefined') {
            apiPayload['environment'] = environment;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'target',
            models: {
                target: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        name: '',
                        userId: '',
                        providerType: '',
                        identifier: '',
                        expired: false,
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a push notification target for the currently logged in user. After deletion, the device will no longer receive push notifications. The target must exist and belong to the current user.
     *
     * @param {string} params.targetId - Target ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    deletePushTarget(params: { targetId: string }): Promise<{}>;
    /**
     * Delete a push notification target for the currently logged in user. After deletion, the device will no longer receive push notifications. The target must exist and belong to the current user.
     *
     * @param {string} targetId - Target ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    deletePushTarget(targetId: string): Promise<{}>;
    deletePushTarget(
        paramsOrFirst: { targetId: string } | string,
    ): Promise<{}> {
        let params: { targetId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { targetId: string };
        } else {
            params = {
                targetId: paramsOrFirst as string,
            };
        }

        const targetId = params.targetId;

        if (typeof targetId === 'undefined' || targetId === '') {
            throw new AppwriteException(
                'Missing required parameter: "targetId"',
            );
        }
        const apiPath = '/account/targets/{targetId}/push'.replace(
            '{targetId}',
            encodeURIComponent(String(targetId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }

    /**
     * Sends the user an email with a secret key for creating a session. If the email address has never been used, a **new account is created** using the provided `userId`. Otherwise, if the email address is already attached to an account, the **user ID is ignored**. Then, the user will receive an email with the one-time password. Use the returned user ID and secret and submit a request to the [POST /v1/account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process. The secret sent to the user's email is valid for 15 minutes.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {string} params.userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars. If the email address has never been used, a new account is created using the provided userId. Otherwise, if the email address is already attached to an account, the user ID is ignored.
     * @param {string} params.email - User email.
     * @param {boolean} params.phrase - Toggle for security phrase. If enabled, email will be send with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createEmailToken(params: {
        userId: string;
        email: string;
        phrase?: boolean;
    }): Promise<Models.Token>;
    /**
     * Sends the user an email with a secret key for creating a session. If the email address has never been used, a **new account is created** using the provided `userId`. Otherwise, if the email address is already attached to an account, the **user ID is ignored**. Then, the user will receive an email with the one-time password. Use the returned user ID and secret and submit a request to the [POST /v1/account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process. The secret sent to the user's email is valid for 15 minutes.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {string} userId - User ID. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars. If the email address has never been used, a new account is created using the provided userId. Otherwise, if the email address is already attached to an account, the user ID is ignored.
     * @param {string} email - User email.
     * @param {boolean} phrase - Toggle for security phrase. If enabled, email will be send with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createEmailToken(
        userId: string,
        email: string,
        phrase?: boolean,
    ): Promise<Models.Token>;
    createEmailToken(
        paramsOrFirst:
            { userId: string; email: string; phrase?: boolean } | string,
        ...rest: [string?, boolean?]
    ): Promise<Models.Token> {
        let params: { userId: string; email: string; phrase?: boolean };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                phrase?: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                phrase: rest[1] as boolean,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const phrase = params.phrase;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        const apiPath = '/account/tokens/email';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof phrase !== 'undefined') {
            apiPayload['phrase'] = phrase;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Sends the user an email with a secret key for creating a session. If the provided user ID has not been registered, a new user will be created. When the user clicks the link in the email, the user is redirected back to the URL you provided with the secret key and userId values attached to the URL query string. Use the query string parameters to submit a request to the [POST /v1/account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process. The link sent to the user's email address is valid for 1 hour.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {string} params.userId - Unique Id. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars. If the email address has never been used, a new account is created using the provided userId. Otherwise, if the email address is already attached to an account, the user ID is ignored.
     * @param {string} params.email - User email.
     * @param {string} params.url - URL to redirect the user back to your app from the magic URL login. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {boolean} params.phrase - Toggle for security phrase. If enabled, email will be send with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createMagicURLToken(params: {
        userId: string;
        email: string;
        url?: string;
        phrase?: boolean;
    }): Promise<Models.Token>;
    /**
     * Sends the user an email with a secret key for creating a session. If the provided user ID has not been registered, a new user will be created. When the user clicks the link in the email, the user is redirected back to the URL you provided with the secret key and userId values attached to the URL query string. Use the query string parameters to submit a request to the [POST /v1/account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process. The link sent to the user's email address is valid for 1 hour.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     *
     * @param {string} userId - Unique Id. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars. If the email address has never been used, a new account is created using the provided userId. Otherwise, if the email address is already attached to an account, the user ID is ignored.
     * @param {string} email - User email.
     * @param {string} url - URL to redirect the user back to your app from the magic URL login. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {boolean} phrase - Toggle for security phrase. If enabled, email will be send with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createMagicURLToken(
        userId: string,
        email: string,
        url?: string,
        phrase?: boolean,
    ): Promise<Models.Token>;
    createMagicURLToken(
        paramsOrFirst:
            | { userId: string; email: string; url?: string; phrase?: boolean }
            | string,
        ...rest: [string?, string?, boolean?]
    ): Promise<Models.Token> {
        let params: {
            userId: string;
            email: string;
            url?: string;
            phrase?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                email: string;
                url?: string;
                phrase?: boolean;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                email: rest[0] as string,
                url: rest[1] as string,
                phrase: rest[2] as boolean,
            };
        }

        const userId = params.userId;
        const email = params.email;
        const url = params.url;
        const phrase = params.phrase;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof email === 'undefined') {
            throw new AppwriteException('Missing required parameter: "email"');
        }
        const apiPath = '/account/tokens/magic-url';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof email !== 'undefined') {
            apiPayload['email'] = email;
        }
        if (typeof url !== 'undefined') {
            apiPayload['url'] = url;
        }
        if (typeof phrase !== 'undefined') {
            apiPayload['phrase'] = phrase;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Allow the user to login to their account using the OAuth2 provider of their choice. Each OAuth2 provider should be enabled from the Appwrite console first. Use the success and failure arguments to provide a redirect URL's back to your app when login is completed.
     *
     * If authentication succeeds, `userId` and `secret` of a token will be appended to the success URL as query parameters. These can be used to create a new session using the [Create session](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint.
     *
     * If there is already an active session, the OAuth2 identity is attached to the logged-in account and that session stays active until the token is exchanged for a new one.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     * @param {OAuthProvider} params.provider - OAuth2 Provider. Currently, supported providers are: amazon, apple, appwrite, auth0, authentik, autodesk, bitbucket, bitly, box, cloudflare, dailymotion, discord, disqus, dropbox, etsy, facebook, figma, fusionauth, github, gitlab, google, huggingface, kakao, keycloak, kick, linkedin, microsoft, notion, oidc, okta, paypal, paypalSandbox, podio, resend, salesforce, slack, spotify, stripe, tiktok, tradeshift, tradeshiftBox, twitch, webflow, wordpress, x, yahoo, yammer, yandex, zoho, zoom.
     * @param {string} params.success - URL to redirect back to your app after a successful login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string} params.failure - URL to redirect back to your app after a failed login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string[]} params.scopes - A list of custom OAuth2 scopes. Check each provider internal docs for a list of supported scopes. Maximum of 100 scopes are allowed, each 4096 characters long.
     * @param {string} params.state - An opaque value your app generates and keeps, for example in a cookie. It is returned unchanged as the `state` query parameter on the success and failure URLs, so your app can check that the sign-in it receives is one it started. Printable ASCII only (RFC 6749 Appendix A.5). Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {void | string}
     */
    createOAuth2Token(params: {
        provider: OAuthProvider;
        success?: string;
        failure?: string;
        scopes?: string[];
        state?: string;
    }): void | string;
    /**
     * Allow the user to login to their account using the OAuth2 provider of their choice. Each OAuth2 provider should be enabled from the Appwrite console first. Use the success and failure arguments to provide a redirect URL's back to your app when login is completed.
     *
     * If authentication succeeds, `userId` and `secret` of a token will be appended to the success URL as query parameters. These can be used to create a new session using the [Create session](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint.
     *
     * If there is already an active session, the OAuth2 identity is attached to the logged-in account and that session stays active until the token is exchanged for a new one.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     * @param {OAuthProvider} provider - OAuth2 Provider. Currently, supported providers are: amazon, apple, appwrite, auth0, authentik, autodesk, bitbucket, bitly, box, cloudflare, dailymotion, discord, disqus, dropbox, etsy, facebook, figma, fusionauth, github, gitlab, google, huggingface, kakao, keycloak, kick, linkedin, microsoft, notion, oidc, okta, paypal, paypalSandbox, podio, resend, salesforce, slack, spotify, stripe, tiktok, tradeshift, tradeshiftBox, twitch, webflow, wordpress, x, yahoo, yammer, yandex, zoho, zoom.
     * @param {string} success - URL to redirect back to your app after a successful login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string} failure - URL to redirect back to your app after a failed login attempt.  Only URLs from hostnames in your project's platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @param {string[]} scopes - A list of custom OAuth2 scopes. Check each provider internal docs for a list of supported scopes. Maximum of 100 scopes are allowed, each 4096 characters long.
     * @param {string} state - An opaque value your app generates and keeps, for example in a cookie. It is returned unchanged as the `state` query parameter on the success and failure URLs, so your app can check that the sign-in it receives is one it started. Printable ASCII only (RFC 6749 Appendix A.5). Max length: 256 chars.
     * @throws {AppwriteException}
     * @returns {void | string}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createOAuth2Token(
        provider: OAuthProvider,
        success?: string,
        failure?: string,
        scopes?: string[],
        state?: string,
    ): void | string;
    createOAuth2Token(
        paramsOrFirst:
            | {
                  provider: OAuthProvider;
                  success?: string;
                  failure?: string;
                  scopes?: string[];
                  state?: string;
              }
            | OAuthProvider,
        ...rest: [string?, string?, string[]?, string?]
    ): void | string {
        let params: {
            provider: OAuthProvider;
            success?: string;
            failure?: string;
            scopes?: string[];
            state?: string;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst) &&
            ('provider' in paramsOrFirst ||
                'success' in paramsOrFirst ||
                'failure' in paramsOrFirst ||
                'scopes' in paramsOrFirst ||
                'state' in paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                provider: OAuthProvider;
                success?: string;
                failure?: string;
                scopes?: string[];
                state?: string;
            };
        } else {
            params = {
                provider: paramsOrFirst as OAuthProvider,
                success: rest[0] as string,
                failure: rest[1] as string,
                scopes: rest[2] as string[],
                state: rest[3] as string,
            };
        }

        const provider = params.provider;
        const success = params.success;
        const failure = params.failure;
        const scopes = params.scopes;
        const state = params.state;

        if (typeof provider === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "provider"',
            );
        }
        const apiPath = '/account/tokens/oauth2/{provider}'.replace(
            '{provider}',
            encodeURIComponent(String(provider)),
        );
        const apiPayload: Payload = {};
        if (typeof success !== 'undefined') {
            apiPayload['success'] = success;
        }
        if (typeof failure !== 'undefined') {
            apiPayload['failure'] = failure;
        }
        if (typeof scopes !== 'undefined') {
            apiPayload['scopes'] = scopes;
        }
        if (typeof state !== 'undefined') {
            apiPayload['state'] = state;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        apiPayload['project'] = this.client.config.project;

        for (const [key, value] of Object.entries(Client.flatten(apiPayload))) {
            uri.searchParams.append(key, value);
        }

        if (typeof window !== 'undefined' && window?.location) {
            window.location.href = uri.toString();
            return;
        } else {
            return uri.toString();
        }
    }

    /**
     * Start signing in with a passkey. No email or user ID is needed: pass the returned `publicKey` options to `navigator.credentials.get()` and the user picks one of their passkeys. Then call [Update passkey token](/docs/references/cloud/client-web/account#updatePasskeyToken) with the credential to receive a token, and exchange it with [Create session](/docs/references/cloud/client-web/account#createSession). The challenge expires after 5 minutes.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.PasskeyChallenge>}
     */
    createPasskeyToken(): Promise<Models.PasskeyChallenge> {
        const apiPath = '/account/tokens/passkey';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'passkeyChallenge',
            models: {
                passkeyChallenge: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        passkeyId: '',
                        expire: '',
                        publicKey: {},
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Complete a passkey sign-in started with [Create passkey token](/docs/references/cloud/client-web/account#createPasskeyToken). Pass the challenge ID and the JSON form of the credential returned by `navigator.credentials.get()`, for example `credential.toJSON()`. The returned token includes its secret and expires after 1 minute: exchange it for a session with [Create session](/docs/references/cloud/client-web/account#createSession). Passkeys require user verification, so the session also satisfies MFA. Each challenge can only be used once.
     *
     * @param {string} params.challengeId - Challenge ID returned by createPasskeyToken.
     * @param {object} params.credential - Authentication credential returned by the authenticator, in the JSON form produced by `PublicKeyCredential.toJSON()`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TokenSecret>}
     */
    updatePasskeyToken(params: {
        challengeId: string;
        credential: object;
    }): Promise<Models.TokenSecret>;
    /**
     * Complete a passkey sign-in started with [Create passkey token](/docs/references/cloud/client-web/account#createPasskeyToken). Pass the challenge ID and the JSON form of the credential returned by `navigator.credentials.get()`, for example `credential.toJSON()`. The returned token includes its secret and expires after 1 minute: exchange it for a session with [Create session](/docs/references/cloud/client-web/account#createSession). Passkeys require user verification, so the session also satisfies MFA. Each challenge can only be used once.
     *
     * @param {string} challengeId - Challenge ID returned by createPasskeyToken.
     * @param {object} credential - Authentication credential returned by the authenticator, in the JSON form produced by `PublicKeyCredential.toJSON()`.
     * @throws {AppwriteException}
     * @returns {Promise<Models.TokenSecret>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePasskeyToken(
        challengeId: string,
        credential: object,
    ): Promise<Models.TokenSecret>;
    updatePasskeyToken(
        paramsOrFirst: { challengeId: string; credential: object } | string,
        ...rest: [object?]
    ): Promise<Models.TokenSecret> {
        let params: { challengeId: string; credential: object };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                challengeId: string;
                credential: object;
            };
        } else {
            params = {
                challengeId: paramsOrFirst as string,
                credential: rest[0] as object,
            };
        }

        const challengeId = params.challengeId;
        const credential = params.credential;

        if (typeof challengeId === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "challengeId"',
            );
        }
        if (typeof credential === 'undefined') {
            throw new AppwriteException(
                'Missing required parameter: "credential"',
            );
        }
        const apiPath = '/account/tokens/passkey';
        const apiPayload: Payload = {};
        if (typeof challengeId !== 'undefined') {
            apiPayload['challengeId'] = challengeId;
        }
        if (typeof credential !== 'undefined') {
            apiPayload['credential'] = credential;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'tokenSecret',
            models: {
                tokenSecret: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Sends the user an SMS with a secret key for creating a session. If the provided user ID has not be registered, a new user will be created. Use the returned user ID and secret and submit a request to the [POST /v1/account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process. The secret sent to the user's phone is valid for 15 minutes.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     * @param {string} params.userId - Unique Id. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars. If the phone number has never been used, a new account is created using the provided userId. Otherwise, if the phone number is already attached to an account, the user ID is ignored.
     * @param {string} params.phone - Phone number. Format this number with a leading '+' and a country code, e.g., +16175551212.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createPhoneToken(params: {
        userId: string;
        phone: string;
    }): Promise<Models.Token>;
    /**
     * Sends the user an SMS with a secret key for creating a session. If the provided user ID has not be registered, a new user will be created. Use the returned user ID and secret and submit a request to the [POST /v1/account/sessions/token](https://appwrite.io/docs/references/cloud/client-web/account#createSession) endpoint to complete the login process. The secret sent to the user's phone is valid for 15 minutes.
     *
     * A user is limited to 10 active sessions at a time by default. [Learn more about session limits](https://appwrite.io/docs/authentication-security#limits).
     *
     * @param {string} userId - Unique Id. Choose a custom ID or generate a random ID with `ID.unique()`. Valid chars are a-z, A-Z, 0-9, period, hyphen, and underscore. Can't start with a special char. Max length is 36 chars. If the phone number has never been used, a new account is created using the provided userId. Otherwise, if the phone number is already attached to an account, the user ID is ignored.
     * @param {string} phone - Phone number. Format this number with a leading '+' and a country code, e.g., +16175551212.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createPhoneToken(userId: string, phone: string): Promise<Models.Token>;
    createPhoneToken(
        paramsOrFirst: { userId: string; phone: string } | string,
        ...rest: [string?]
    ): Promise<Models.Token> {
        let params: { userId: string; phone: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { userId: string; phone: string };
        } else {
            params = {
                userId: paramsOrFirst as string,
                phone: rest[0] as string,
            };
        }

        const userId = params.userId;
        const phone = params.phone;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof phone === 'undefined') {
            throw new AppwriteException('Missing required parameter: "phone"');
        }
        const apiPath = '/account/tokens/phone';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof phone !== 'undefined') {
            apiPayload['phone'] = phone;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to send a verification message to your user email address to confirm they are the valid owners of that address. Both the **userId** and **secret** arguments will be passed as query parameters to the URL you have provided to be attached to the verification email. The provided URL should redirect the user back to your app and allow you to complete the verification process by verifying both the **userId** and **secret** parameters. Learn more about how to [complete the verification process](https://appwrite.io/docs/references/cloud/client-web/account#updateVerification). The verification link sent to the user's email address is valid for 7 days.
     *
     * Please note that in order to avoid a [Redirect Attack](https://github.com/OWASP/CheatSheetSeries/blob/master/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.md), the only valid redirect URLs are the ones from domains you have set when adding your platforms in the console interface.
     *
     *
     * @param {string} params.url - URL to redirect the user back to your app from the verification email. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createEmailVerification(params: { url: string }): Promise<Models.Token>;
    /**
     * Use this endpoint to send a verification message to your user email address to confirm they are the valid owners of that address. Both the **userId** and **secret** arguments will be passed as query parameters to the URL you have provided to be attached to the verification email. The provided URL should redirect the user back to your app and allow you to complete the verification process by verifying both the **userId** and **secret** parameters. Learn more about how to [complete the verification process](https://appwrite.io/docs/references/cloud/client-web/account#updateVerification). The verification link sent to the user's email address is valid for 7 days.
     *
     * Please note that in order to avoid a [Redirect Attack](https://github.com/OWASP/CheatSheetSeries/blob/master/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.md), the only valid redirect URLs are the ones from domains you have set when adding your platforms in the console interface.
     *
     *
     * @param {string} url - URL to redirect the user back to your app from the verification email. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createEmailVerification(url: string): Promise<Models.Token>;
    createEmailVerification(
        paramsOrFirst: { url: string } | string,
    ): Promise<Models.Token> {
        let params: { url: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { url: string };
        } else {
            params = {
                url: paramsOrFirst as string,
            };
        }

        const url = params.url;

        if (typeof url === 'undefined') {
            throw new AppwriteException('Missing required parameter: "url"');
        }
        const apiPath = '/account/verifications/email';
        const apiPayload: Payload = {};
        if (typeof url !== 'undefined') {
            apiPayload['url'] = url;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to send a verification message to your user email address to confirm they are the valid owners of that address. Both the **userId** and **secret** arguments will be passed as query parameters to the URL you have provided to be attached to the verification email. The provided URL should redirect the user back to your app and allow you to complete the verification process by verifying both the **userId** and **secret** parameters. Learn more about how to [complete the verification process](https://appwrite.io/docs/references/cloud/client-web/account#updateVerification). The verification link sent to the user's email address is valid for 7 days.
     *
     * Please note that in order to avoid a [Redirect Attack](https://github.com/OWASP/CheatSheetSeries/blob/master/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.md), the only valid redirect URLs are the ones from domains you have set when adding your platforms in the console interface.
     *
     *
     * @param {string} params.url - URL to redirect the user back to your app from the verification email. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.createEmailVerification` instead.
     */
    createVerification(params: { url: string }): Promise<Models.Token>;
    /**
     * Use this endpoint to send a verification message to your user email address to confirm they are the valid owners of that address. Both the **userId** and **secret** arguments will be passed as query parameters to the URL you have provided to be attached to the verification email. The provided URL should redirect the user back to your app and allow you to complete the verification process by verifying both the **userId** and **secret** parameters. Learn more about how to [complete the verification process](https://appwrite.io/docs/references/cloud/client-web/account#updateVerification). The verification link sent to the user's email address is valid for 7 days.
     *
     * Please note that in order to avoid a [Redirect Attack](https://github.com/OWASP/CheatSheetSeries/blob/master/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.md), the only valid redirect URLs are the ones from domains you have set when adding your platforms in the console interface.
     *
     *
     * @param {string} url - URL to redirect the user back to your app from the verification email. Only URLs from hostnames in your project platform list are allowed. This requirement helps to prevent an [open redirect](https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html) attack against your project API.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createVerification(url: string): Promise<Models.Token>;
    createVerification(
        paramsOrFirst: { url: string } | string,
    ): Promise<Models.Token> {
        let params: { url: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { url: string };
        } else {
            params = {
                url: paramsOrFirst as string,
            };
        }

        const url = params.url;

        if (typeof url === 'undefined') {
            throw new AppwriteException('Missing required parameter: "url"');
        }
        const apiPath = '/account/verifications/email';
        const apiPayload: Payload = {};
        if (typeof url !== 'undefined') {
            apiPayload['url'] = url;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to complete the user email verification process. Use both the **userId** and **secret** parameters that were attached to your app URL to verify the user email ownership. If confirmed this route will return a 200 status code.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    updateEmailVerification(params: {
        userId: string;
        secret: string;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to complete the user email verification process. Use both the **userId** and **secret** parameters that were attached to your app URL to verify the user email ownership. If confirmed this route will return a 200 status code.
     *
     * @param {string} userId - User ID.
     * @param {string} secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateEmailVerification(
        userId: string,
        secret: string,
    ): Promise<Models.Token>;
    updateEmailVerification(
        paramsOrFirst: { userId: string; secret: string } | string,
        ...rest: [string?]
    ): Promise<Models.Token> {
        let params: { userId: string; secret: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        const apiPath = '/account/verifications/email';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to complete the user email verification process. Use both the **userId** and **secret** parameters that were attached to your app URL to verify the user email ownership. If confirmed this route will return a 200 status code.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated This API has been deprecated since 1.8.0. Please use `Account.updateEmailVerification` instead.
     */
    updateVerification(params: {
        userId: string;
        secret: string;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to complete the user email verification process. Use both the **userId** and **secret** parameters that were attached to your app URL to verify the user email ownership. If confirmed this route will return a 200 status code.
     *
     * @param {string} userId - User ID.
     * @param {string} secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateVerification(userId: string, secret: string): Promise<Models.Token>;
    updateVerification(
        paramsOrFirst: { userId: string; secret: string } | string,
        ...rest: [string?]
    ): Promise<Models.Token> {
        let params: { userId: string; secret: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        const apiPath = '/account/verifications/email';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to send a 6-digit verification code to the currently logged in user's email address. Unlike [createEmailVerification](https://appwrite.io/docs/references/cloud/client-web/account#createEmailVerification), this method requires no redirect URL, which makes it suitable for mobile and desktop apps that cannot host a verification page. Learn more about how to [complete the verification process](https://appwrite.io/docs/references/cloud/client-web/account#updateEmailVerificationOTP). The code sent to the user's email address is valid for 15 minutes.
     *
     * Enable the **phrase** parameter to include a randomly generated security phrase in both the email and the response. Showing that phrase in your app lets the user confirm the email genuinely came from your request, which helps protect against phishing.
     *
     *
     * @param {boolean} params.phrase - Toggle for security phrase. If enabled, email will be sent with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createEmailVerificationOTP(params?: {
        phrase?: boolean;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to send a 6-digit verification code to the currently logged in user's email address. Unlike [createEmailVerification](https://appwrite.io/docs/references/cloud/client-web/account#createEmailVerification), this method requires no redirect URL, which makes it suitable for mobile and desktop apps that cannot host a verification page. Learn more about how to [complete the verification process](https://appwrite.io/docs/references/cloud/client-web/account#updateEmailVerificationOTP). The code sent to the user's email address is valid for 15 minutes.
     *
     * Enable the **phrase** parameter to include a randomly generated security phrase in both the email and the response. Showing that phrase in your app lets the user confirm the email genuinely came from your request, which helps protect against phishing.
     *
     *
     * @param {boolean} phrase - Toggle for security phrase. If enabled, email will be sent with a randomly generated phrase and the phrase will also be included in the response. Confirming phrases match increases the security of your authentication flow.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    createEmailVerificationOTP(phrase?: boolean): Promise<Models.Token>;
    createEmailVerificationOTP(
        paramsOrFirst?: { phrase?: boolean } | boolean,
    ): Promise<Models.Token> {
        let params: { phrase?: boolean };

        if (
            typeof paramsOrFirst === 'undefined' ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as { phrase?: boolean };
        } else {
            params = {
                phrase: paramsOrFirst as boolean,
            };
        }

        const phrase = params.phrase;

        const apiPath = '/account/verifications/email/otp';
        const apiPayload: Payload = {};
        if (typeof phrase !== 'undefined') {
            apiPayload['phrase'] = phrase;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to complete the user email verification process using the 6-digit code that was emailed by [createEmailVerificationOTP](https://appwrite.io/docs/references/cloud/client-web/account#createEmailVerificationOTP). Pass the **userId** of the user being verified along with the **secret** code from the email. If confirmed, this route will return a 200 status code and the code is consumed.
     *
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.secret - Valid verification OTP code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    updateEmailVerificationOTP(params: {
        userId: string;
        secret: string;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to complete the user email verification process using the 6-digit code that was emailed by [createEmailVerificationOTP](https://appwrite.io/docs/references/cloud/client-web/account#createEmailVerificationOTP). Pass the **userId** of the user being verified along with the **secret** code from the email. If confirmed, this route will return a 200 status code and the code is consumed.
     *
     *
     * @param {string} userId - User ID.
     * @param {string} secret - Valid verification OTP code.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updateEmailVerificationOTP(
        userId: string,
        secret: string,
    ): Promise<Models.Token>;
    updateEmailVerificationOTP(
        paramsOrFirst: { userId: string; secret: string } | string,
        ...rest: [string?]
    ): Promise<Models.Token> {
        let params: { userId: string; secret: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        const apiPath = '/account/verifications/email/otp';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to send a verification SMS to the currently logged in user. This endpoint is meant for use after updating a user's phone number using the [accountUpdatePhone](https://appwrite.io/docs/references/cloud/client-web/account#updatePhone) endpoint. Learn more about how to [complete the verification process](https://appwrite.io/docs/references/cloud/client-web/account#updatePhoneVerification). The verification code sent to the user's phone number is valid for 15 minutes.
     *
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    createPhoneVerification(): Promise<Models.Token> {
        const apiPath = '/account/verifications/phone';
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('post', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Use this endpoint to complete the user phone verification process. Use the **userId** and **secret** that were sent to your user's phone number to verify the user email ownership. If confirmed this route will return a 200 status code.
     *
     * @param {string} params.userId - User ID.
     * @param {string} params.secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     */
    updatePhoneVerification(params: {
        userId: string;
        secret: string;
    }): Promise<Models.Token>;
    /**
     * Use this endpoint to complete the user phone verification process. Use the **userId** and **secret** that were sent to your user's phone number to verify the user email ownership. If confirmed this route will return a 200 status code.
     *
     * @param {string} userId - User ID.
     * @param {string} secret - Valid verification token.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Token>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    updatePhoneVerification(
        userId: string,
        secret: string,
    ): Promise<Models.Token>;
    updatePhoneVerification(
        paramsOrFirst: { userId: string; secret: string } | string,
        ...rest: [string?]
    ): Promise<Models.Token> {
        let params: { userId: string; secret: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                userId: string;
                secret: string;
            };
        } else {
            params = {
                userId: paramsOrFirst as string,
                secret: rest[0] as string,
            };
        }

        const userId = params.userId;
        const secret = params.secret;

        if (typeof userId === 'undefined') {
            throw new AppwriteException('Missing required parameter: "userId"');
        }
        if (typeof secret === 'undefined') {
            throw new AppwriteException('Missing required parameter: "secret"');
        }
        const apiPath = '/account/verifications/phone';
        const apiPayload: Payload = {};
        if (typeof userId !== 'undefined') {
            apiPayload['userId'] = userId;
        }
        if (typeof secret !== 'undefined') {
            apiPayload['secret'] = secret;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'token',
            models: {
                token: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        userId: '',
                        secret: '',
                        expire: '',
                        phrase: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }
}
